import type { MessagePack } from "./en.ts";

/**
 * The Thai pack.
 *
 * **Every string here awaits a native reviewer** — see `TH_AWAITING_REVIEW` at
 * the foot of the file, and `locales.ts`, where `th` is `review_required` for
 * this reason. It is registered and served rather than withheld because the
 * alternative is an English till in a Thai restaurant; a pack under review is
 * the state `es` shipped in for the same reason.
 *
 * Two things a reviewer should look at first, because they are where a
 * translator without the product in front of them goes wrong:
 *
 * - **Thai has no spaces between words**, so a label that reads as one phrase
 *   in English can read as a different compound here. The screens most at risk
 *   are the ones with two nouns stacked — "Print log", "Shift report".
 * - **The formal/informal register.** These are addressed to a restaurant's
 *   own staff on their own till, so the plain register is used throughout
 *   rather than the polite particles (ครับ/ค่ะ) a customer-facing app would
 *   take. A reviewer may disagree; it is one decision applied consistently.
 *
 * Placeholders (`{count}`, `{name}`) carry through untouched and are checked by
 * the key set, not by anything here.
 */
export const th = {
  "app.name": "TOVI",
  "app.description": "ระบบหลังร้านอาหาร",
  "app.operatorName": "TOVI Operator",
  "app.operatorDescription": "คอนโซลผู้ดูแลระบบของแพลตฟอร์ม",

  "nav.dashboard": "แดชบอร์ด",
  "nav.menu": "เมนู",
  "nav.operations": "การดำเนินงาน",
  "nav.staff": "พนักงาน",
  "nav.roles": "บทบาท",
  "nav.stations": "จุดผลิต",
  "nav.printers": "เครื่องพิมพ์",
  "nav.pairTill": "จับคู่เครื่องขาย",
  "nav.printLog": "ประวัติการพิมพ์",
  "nav.receiptTemplates": "แม่แบบใบเสร็จ",
  "nav.banners": "แบนเนอร์",
  "nav.notifications": "การแจ้งเตือน",
  "nav.operationLog": "บันทึกการใช้งาน",
  "nav.inventory": "สินค้าคงคลัง",
  "nav.supplies": "วัสดุ",
  "nav.suppliers": "ผู้จัดจำหน่าย",
  "nav.warehouses": "คลังสินค้า",
  "nav.units": "หน่วยนับ",
  "nav.supplyCategories": "หมวดวัสดุ",
  "nav.brands": "แบรนด์",
  "nav.chain": "เครือร้าน",
  "nav.substores": "สาขา",
  "nav.invoice": "ใบกำกับภาษี",
  "nav.trading": "การขาย",

  "staff.title": "พนักงาน",
  "staff.description": "ใครทำงานที่นี่ ทำอะไรได้บ้าง และใครใช้เครื่องขายได้",
  "staff.searchPlaceholder": "ค้นหาด้วยชื่อ ชื่อผู้ใช้ หรือรหัส",
  "staff.allRoles": "ทุกบทบาท",
  "staff.noRole": "ไม่มีบทบาท",
  "staff.allStatuses": "ทุกสถานะ",
  "staff.tillAccess": "สิทธิ์ใช้เครื่องขาย",
  "staff.allTillAccess": "ทุกสิทธิ์ใช้เครื่องขาย",
  "staff.tillAllowed": "ใช้เครื่องขายได้",
  "staff.tillDenied": "ใช้เครื่องขายไม่ได้",
  "staff.new": "เพิ่มพนักงาน",
  "staff.edit": "แก้ไขพนักงาน",
  "staff.record": "ประวัติ",
  "staff.name": "ชื่อ",
  "staff.username": "ชื่อผู้ใช้",
  "staff.number": "รหัสพนักงาน",
  "staff.role": "บทบาท",
  "staff.cashRegister": "ใช้เครื่องขายได้",
  "staff.cashRegisterHint": "แยกจากบทบาท พนักงานรับออร์เดอร์ได้โดยไม่ต้องรับเงินก็ได้",
  "staff.posPermissions": "สิทธิ์บนเครื่องขาย",
  "staff.inventoryPermissions": "สิทธิ์สินค้าคงคลัง",
  "staff.inventoryHint": "ให้แยกต่างหากจากสิทธิ์ใช้เครื่องขาย",
  "staff.deleteTitle": "นำพนักงานคนนี้ออกหรือไม่",
  "staff.deleteBody": "เขาจะเข้าสู่ระบบเครื่องขายไม่ได้อีก ออร์เดอร์ที่เคยรับไว้ยังคงชื่อเดิม",
  "staff.photo": "รูปภาพ",
  "staff.photoHint": "แสดงข้างชื่อในรายการ",
  "staff.photoAdd": "เพิ่มรูปภาพ",
  "staff.photoReplace": "เปลี่ยนรูปภาพ",
  "staff.photoRemove": "ลบรูปภาพ",
  "staff.viewPhoto": "ดูรูปภาพ",
  "staff.cropTitle": "ครอบตัดรูปภาพ",
  "staff.cropHint": "ลากเพื่อจัดตำแหน่ง เก็บเฉพาะส่วนที่อยู่ในวงกลมเท่านั้น",
  "staff.zoom": "ซูม",
  "staff.cropConfirm": "ใช้รูปนี้",
  "staff.removePhotoTitle": "ลบรูปภาพนี้หรือไม่",
  "staff.removePhotoBody": "จะแสดงอักษรย่อแทนจนกว่าจะมีการเพิ่มรูปใหม่",

  "staff.setPassword": "ตั้งรหัสผ่าน",
  "staff.password": "รหัสผ่าน",
  "staff.passwordConfirm": "ยืนยันรหัสผ่าน",
  "staff.passwordHint":
    "อย่างน้อยแปดตัวอักษร ใช้ชื่อผู้ใช้และรหัสผ่านนี้เพื่อเข้าสู่ระบบแอปพนักงาน",
  "staff.passwordMismatch": "รหัสผ่านทั้งสองไม่ตรงกัน",
  "staff.passwordSaved":
    "ตั้งรหัสผ่านแล้ว ออกจากระบบทุกที่ให้แล้ว และเข้าสู่ระบบด้วยรหัสผ่านใหม่ได้",

  "roles.title": "บทบาท",
  "roles.description": "แต่ละตำแหน่งงานทำอะไรได้บ้าง",
  "roles.new": "เพิ่มบทบาท",
  "roles.members": "ผู้ใช้",
  "roles.permissions": "สิทธิ์",
  "roles.seeded": "มีมาให้",
  "roles.granted": "ให้แล้ว {count} รายการ",
  "roles.record": "บทบาท",
  "roles.code": "รหัส",
  "roles.note": "หมายเหตุ",
  "roles.noneGranted": "ไม่ได้ให้สิทธิ์ใด",
  "roles.catalogueFailed": "โหลดรายการสิทธิ์ไม่สำเร็จ จึงแสดงแต่ละสิทธิ์ด้วยคีย์",

  "stations.title": "จุดผลิต",
  "stations.description": "จุดที่ทำงานจริง รายการจะส่งไปที่จุดผลิต ไม่ใช่ที่เครื่องพิมพ์",
  "stations.new": "เพิ่มจุดผลิต",
  "stations.record": "ประวัติ",
  "stations.name": "ชื่อ",
  "stations.nameHint": "ชื่อที่คนหน้างานเรียกกัน เช่น ครัวร้อน บาร์ ของหวาน",
  "stations.printers": "เครื่องพิมพ์",
  "stations.noPrinters": "ไม่มีเครื่องพิมพ์",
  "stations.noPrintersHint": "จุดผลิตที่ไม่มีเครื่องพิมพ์ยังรับรายการได้ แต่จะไม่พิมพ์อะไรออกมา",
  "stations.deleteTitle": "ลบจุดผลิตนี้หรือไม่",
  "stations.deleteBody":
    "ลบได้เฉพาะจุดผลิตที่ไม่มีอะไรอ้างถึง เครื่องพิมพ์ที่ประจำอยู่และรายการที่ส่งมาที่นี่ต้องย้ายออกก่อน",
  "printers.title": "เครื่องพิมพ์",
  "printers.description": "อุปกรณ์ที่จับคู่ไว้ และแต่ละเครื่องประจำอยู่จุดผลิตใด",
  "printers.new": "เพิ่มเครื่องพิมพ์",
  "printers.record": "ประวัติ",
  "printers.name": "ชื่อ",
  "printers.model": "รุ่น",
  "printers.connection": "การเชื่อมต่อ",
  "printers.address": "ที่อยู่",
  "printers.addressHint": "IP และพอร์ตสำหรับ LAN หรือที่อยู่ MAC สำหรับบลูทูธ",
  "printers.role": "พิมพ์",
  "printers.station": "จุดผลิต",
  "printers.stationHint": "ทุกอย่างที่ส่งไปจุดผลิตนี้จะพิมพ์ที่นี่",
  "printers.stationNone": "เลือกจุดผลิต",
  "printers.copies": "จำนวนชุด",
  "printers.bind": "จับคู่",
  "printers.unbind": "ยกเลิกการจับคู่",
  "printers.status": "สถานะ",
  "printers.deleteTitle": "นำเครื่องพิมพ์นี้ออกหรือไม่",
  "printers.deleteBody":
    "ทุกอย่างที่ส่งมาที่เครื่องนี้จะหยุดพิมพ์ เครื่องพิมพ์ที่รายการใดระบุเป็นอุปกรณ์เดียวของจุดผลิตจะนำออกไม่ได้",
  "printerRole.receipt": "ใบเสร็จ",
  "printerRole.station": "ใบสั่งงานครัว",
  "printerRole.label": "ป้ายสินค้า",
  "printerStatus.unbound": "ยังไม่จับคู่",
  "printerStatus.online": "ออนไลน์",
  "printerStatus.offline": "ออฟไลน์",
  "printerStatus.error": "ผิดพลาด",

  "printLog.title": "ประวัติการพิมพ์",
  "printLog.description": "ทุกเอกสารที่ส่งไปยังเครื่องพิมพ์ และผลว่าถึงหรือไม่",
  "printLog.document": "เอกสาร",
  "printLog.printer": "เครื่องพิมพ์",
  "printLog.queued": "เข้าคิวเมื่อ",
  "printLog.attempts": "จำนวนครั้งที่ลอง",
  "printLog.reprint": "พิมพ์ใหม่",
  "printLog.failedHint": "ใบสั่งงานครัวที่พิมพ์ไม่สำเร็จ คือออร์เดอร์ที่ไม่มีใครทำ",
  "printLog.record": "งานพิมพ์",
  "printLog.reference": "อ้างอิง",
  "printLog.printed": "พิมพ์แล้ว",
  "printLog.failure": "สาเหตุที่ไม่สำเร็จ",
  "printJobStatus.queued": "รอคิว",
  "printJobStatus.printing": "กำลังพิมพ์",
  "printJobStatus.printed": "พิมพ์แล้ว",
  "printJobStatus.failed": "ไม่สำเร็จ",
  "printJobStatus.cancelled": "ยกเลิกแล้ว",
  "documentType.customer_receipt": "ใบเสร็จลูกค้า",
  "documentType.station_ticket": "ใบสั่งงานครัว",
  "documentType.label": "ป้ายสินค้า",
  "documentType.shift_report": "รายงานกะ",
  "documentType.drawer_kick": "เปิดลิ้นชักเงิน",
  "templates.title": "แม่แบบใบเสร็จ",
  "templates.description": "พิมพ์อะไร ภาษาใด และส่งไปยังเครื่องพิมพ์อย่างไร",
  "templates.new": "เพิ่มแม่แบบ",
  "templates.language": "ภาษา",
  "templates.lineWidth": "ตัวอักษรต่อบรรทัด",
  "templates.lineWidthHint": "32 สำหรับกระดาษ 58 มม. และ 48 สำหรับ 80 มม.",
  "templates.renderMode": "ส่งเป็น",
  "templates.text": "ข้อความ",
  "templates.raster": "รูปภาพ",
  "templates.auto": "ให้ภาษาเป็นตัวกำหนด",
  "templates.rasterForced": "ภาษานี้พิมพ์เป็นรูปภาพ",
  "templates.rasterReason.complexScript":
    "{script} พิมพ์จากฟอนต์ในตัวเครื่องพิมพ์ความร้อนไม่ได้ ใบเสร็จนี้จึงส่งเป็นรูปภาพ ซึ่งช้ากว่าเมื่อส่งผ่านบลูทูธเทียบกับสายสัญญาณ",
  "templates.rasterReason.templateChoice": "แม่แบบนี้พิมพ์เป็นรูปภาพเสมอ",
  "templates.preview": "ตัวอย่าง",
  "templates.showLogo": "แสดงโลโก้",
  "templates.headerText": "ส่วนหัว",
  "templates.footerText": "ส่วนท้าย",
  "templates.showTaxBreakdown": "แสดงรายละเอียดภาษี",
  "templates.showTaxRegistration": "แสดงข้อมูลจดทะเบียนภาษี",
  "templates.showItemCodes": "แสดงรหัสสินค้า",
  "templates.showServiceCharge": "แสดงค่าบริการ",
  "templates.showSecondCurrency": "แสดงสกุลเงินที่สอง",
  "templates.showQrCode": "แสดงรหัส QR",

  "banners.title": "แบนเนอร์",
  "banners.description": "โฆษณาบนเมนูสั่งอาหารผ่าน QR",
  "banners.new": "เพิ่มแบนเนอร์",
  "banners.record": "ประวัติ",
  "banners.image": "ที่อยู่รูปภาพ",
  "banners.link": "ลิงก์",
  "banners.schedule": "แสดงช่วง",
  "banners.startsAt": "ตั้งแต่",
  "banners.endsAt": "ถึง",
  "banners.live": "กำลังแสดง",
  "banners.notLive": "ไม่ได้แสดง",
  "banners.deleteTitle": "ลบแบนเนอร์นี้หรือไม่",
  "banners.deleteBody": "แบนเนอร์จะหายไปจากเมนูสั่งอาหารผ่าน QR ทันที",

  "alerts.title": "การแจ้งเตือน",
  "alerts.description": "ทีมงานจะได้รับแจ้งเรื่องอะไร และด้วยวิธีใด",
  "alerts.push": "แจ้งเตือนแบบพุช",
  "alerts.voice": "อ่านออกเสียงที่เครื่องขาย",
  "alerts.event": "เหตุการณ์",
  "alerts.language": "อ่านเป็นภาษา",
  "alerts.repeat": "อ่านซ้ำ",
  "alerts.repeatHint": "จุดผลิตที่งานยุ่งเป็นที่ที่เสียงดังอยู่แล้ว",
  "event.order.new": "มีออร์เดอร์ใหม่เข้ามา",
  "event.order.paid": "ออร์เดอร์ชำระเงินแล้ว",
  "event.order.cancelled": "ออร์เดอร์ถูกยกเลิก",
  "event.order.refunded": "ออร์เดอร์ถูกคืนเงิน",
  "event.print.failed": "พิมพ์เอกสารไม่สำเร็จ",
  "event.shift.warning": "มีกะที่ต้องตรวจสอบ",
  "event.stock.sold_out": "สินค้าหมด",

  "pairTill.title": "จับคู่เครื่องขาย",
  "pairTill.description": "เครื่องขายจะแสดงรหัส 8 ตัวอักษร ป้อนรหัสนี้ที่นี่เพื่ออนุมัติหรือปฏิเสธ",
  "pairTill.codeLabel": "รหัสเครื่องขาย",
  "pairTill.lookUp": "ค้นหา",
  "pairTill.sessionNotice":
    "เครื่องขายจะเข้าสู่ระบบด้วยบัญชีของคุณ จนกว่าจะมีคนเข้าสู่ระบบที่เครื่องนั้น",
  "pairTill.approve": "จับคู่เครื่องขายนี้",
  "pairTill.deny": "ปฏิเสธ",
  "pairTill.approved": "จับคู่เครื่องขายแล้ว เครื่องจะดำเนินการต่อเองภายในไม่กี่วินาที",
  "pairTill.denied": "ปฏิเสธเครื่องขายนี้แล้ว",
  "pairTill.pairAnother": "จับคู่เครื่องขายอีกเครื่อง",
  "pairTill.notFound": "ไม่มีเครื่องขายที่แสดงรหัสนั้น",
  "pairTill.expired": "รหัสนั้นหมดอายุแล้ว",
  "pairTill.forbidden": "คุณไม่มีสิทธิ์จับคู่เครื่องขาย",
  "pairTill.alreadyUsed": "รหัสนี้ถูกใช้ไปแล้ว",
  "pairTill.claimedElsewhere": "มีคนอื่นกำลังดูรหัสนี้อยู่แล้ว",
  "pairTill.identify": "รหัสนี้ใช้ระบุตัวบุคคล ไม่ใช่เครื่องขาย จึงไม่สามารถอนุมัติที่นี่ได้",

  "operationLog.title": "บันทึกการใช้งาน",
  "operationLog.description": "ใครแก้อะไร เมื่อไร บันทึกนี้แก้ไขหรือลบไม่ได้",
  "operationLog.when": "เมื่อ",
  "operationLog.who": "ผู้ดำเนินการ",
  "operationLog.what": "การกระทำ",
  "operationLog.entity": "สิ่งที่ถูกแก้",
  "operationLog.fields": "ช่องที่เปลี่ยน",
  "operationLog.record": "ประวัติ",
  "operationLog.actorKind": "ดำเนินการในฐานะ",
  "operationLog.entityKind": "ประเภทข้อมูล",
  "operationLog.recordId": "รหัสข้อมูล",
  "operationLog.fieldsHint": "ค่าก่อนและหลังถูกเก็บไว้กับรายการนี้ แต่ไม่ได้แสดงที่นี่",
  "actorKind.merchant": "ร้านค้า",
  "actorKind.operator": "ผู้ดูแลแพลตฟอร์ม",

  "trading.title": "การขาย",
  "trading.description": "เวลาทำการ พฤติกรรมของออร์เดอร์ และสิ่งที่แสดงบนเครื่อง",
  "trading.hours": "เวลาทำการ",
  "trading.openTime": "เปิด",
  "trading.closeTime": "ปิด",
  "trading.hoursHint": "เวลาทำการเป็นตัวกำหนดว่ายอดขายอยู่ในวันขายใด จึงมีผลกับทุกรายงาน",
  "trading.status": "สถานะร้าน",
  "trading.hoursLog": "การเปลี่ยนเวลาทำการ",
  "trading.ordering": "การสั่งอาหาร",
  "trading.payFirst": "ลูกค้าชำระเงินก่อนได้รับอาหาร",
  "trading.requirePax": "ถามจำนวนผู้ทานทุกครั้ง",
  "trading.cartSharing": "ลูกค้าหลายคนสั่งรวมในออร์เดอร์เดียวได้",
  "trading.autoCancel": "ยกเลิกออร์เดอร์ที่ยังไม่รับ หลังจาก",
  "trading.minutes": "นาที",
  "trading.printing": "การพิมพ์",
  "trading.autoPrintReceipt": "พิมพ์ใบเสร็จอัตโนมัติเมื่อชำระเงิน",
  "trading.staffTerminalPrinter": "เครื่องพนักงานพิมพ์ตรงไปยังเครื่องพิมพ์ Wi-Fi",
  "trading.staffTerminalPrinterHint":
    "เปิดเมื่อปิดเครื่องขายแล้วเท่านั้น หากเชื่อมต่อทั้งสองเครื่อง เครื่องขายจะหยุดพิมพ์ใบเสร็จ",
  "trading.display": "สิ่งที่แสดงบนเครื่อง",
  "trading.displayTables": "แสดงผังโต๊ะ",
  "delivery.title": "แพลตฟอร์มเดลิเวอรี",
  "delivery.description": "แพลตฟอร์มที่ร้านขายผ่าน และส่วนแบ่งที่แต่ละรายเก็บ",
  "delivery.platforms": "แพลตฟอร์ม",
  "delivery.name": "แสดงชื่อว่า",
  "delivery.commission": "ค่าคอมมิชชัน",
  "delivery.commissionHint": "เป็นเปอร์เซ็นต์ ใส่ 15 หมายถึง 15%",
  "delivery.noneInMarket": "ตลาดนี้ยังไม่มีแพลตฟอร์มเดลิเวอรีให้เลือก",
  "trading.delivers": "สั่งกลับบ้านจัดส่งได้",
  "trading.deliveryFee": "ค่าจัดส่ง",
  "trading.autoAccept": "รับออร์เดอร์โดยไม่ต้องยืนยัน",
  "trading.autoAcceptHint":
    "ออร์เดอร์จากช่องทางที่ติ๊กไว้จะส่งไปจุดผลิตทันที ช่องทางอื่นรอให้มีคนตรวจก่อน",
  "trading.showItemSales": "แสดงยอดขายรายการนั้นให้ลูกค้าเห็น",
  "trading.voiceBroadcast": "อ่านออร์เดอร์ใหม่ออกเสียงที่เครื่องขาย",
  "orderSource.cashier": "เครื่องขาย",
  "orderSource.staff_app": "แอปพนักงาน",
  "orderSource.qr": "สั่งผ่าน QR",
  "orderSource.delivery": "เดลิเวอรี",
  "orderSource.back_office": "ระบบหลังร้าน",
  "trading.showItemImages": "แสดงรูปสินค้าบนเครื่องขาย",
  "trading.showRemainingStock": "แสดงจำนวนคงเหลือ",
  "trading.geofence": "ระยะที่สั่งได้",
  "trading.geofenceHint":
    "ลูกค้าต้องอยู่ในระยะนี้จึงจะสั่งได้ หน่วยเป็นเมตร ค่าน้อยที่สุดที่ตั้งได้คือ {min}",
  "trading.pickupNumbers": "หมายเลขรับอาหาร",
  "trading.pickupHint": "วนหมายเลขใหม่ทุกวันในช่วงนี้ ตลาดของคุณกำหนดไว้ที่ {min} ถึง {max}",
  "chain.title": "สาขา",
  "chain.description": "ทุกสาขาในเครือ และแต่ละแห่งตั้งค่าอะไรเองได้บ้าง",
  "chain.new": "เพิ่มสาขา",
  "chain.headOffice": "สำนักงานใหญ่",
  "chain.branch": "สาขา",
  "chain.name": "ชื่อ",
  "chain.address": "ที่อยู่",
  "chain.slug": "ที่อยู่เว็บ",
  "chain.slugHint": "ใช้กับเว็บสั่งอาหารผ่าน QR ใช้ตัวพิมพ์เล็ก ตัวเลข และขีดกลาง",
  "chain.phone": "โทรศัพท์",
  "chain.timezone": "เขตเวลา",
  "chain.hours": "เวลาทำการ",
  "chain.currency": "สกุลเงิน",
  "chain.items": "รายการ",
  "chain.tables": "โต๊ะ",
  "chain.staff": "พนักงาน",
  "chain.record": "ประวัติ",
  "chain.switchTo": "สลับไปสาขานี้",
  "chain.capabilities": "สิ่งที่สาขานี้ตั้งค่าเองได้",
  "chain.capabilitiesHint":
    "หากไม่ให้ข้อใด สาขายังมีสิทธิ์นั้นอยู่แต่ใช้งานไม่ได้ สำนักงานใหญ่จะเป็นผู้ตั้งค่าแทน",
  "chain.capabilitiesSaved": "บันทึกแล้ว",

  "shell.switchStore": "สลับสาขา",
  "nav.chainMenu": "ส่งเมนู",
  "nav.chainUsers": "สิทธิ์เข้าถึงสาขา",
  "action.done": "เสร็จสิ้น",
  "chainMenu.title": "เมนูสำนักงานใหญ่",
  "chainMenu.description":
    "เมนูที่สำนักงานใหญ่เป็นเจ้าของ เมื่อส่งรายการไปยังสาขา ทั้งสองจะผูกกันไว้จนกว่าสาขานั้นจะแก้ไขฉบับของตนเอง",
  "chainMenu.sku": "รหัสสินค้า",
  "chainMenu.categories": "หมวดหมู่",
  "chainMenu.price": "ราคา",
  "chainMenu.status": "สถานะ",
  "chainMenu.pushedTo": "สาขา",
  "chainMenu.record": "ประวัติ",
  "chainMenu.push": "ส่งไปยังสาขา",
  "chainMenu.pushCount": "ส่ง {count} รายการ",
  "chainMenu.pushTitle": "ส่ง {count} รายการ",
  "chainMenu.pushHint":
    "เลือกสาขาที่จะส่งไป รายการที่สาขาเพิ่มเองจะไม่ถูกแตะต้อง และราคาจะส่งเฉพาะระหว่างสาขาที่ใช้สกุลเงินเดียวกัน",
  "chainMenu.pushing": "กำลังส่ง…",
  "chainMenu.noTargets": "เครือนี้ไม่มีสาขาอื่นนอกจากสำนักงานใหญ่",
  "chainMenu.overwriteLocal": "เขียนทับการแก้ไขของสาขา",
  "chainMenu.overwriteLocalHint":
    "ปิดไว้เป็นค่าเริ่มต้น สาขาที่แก้ไขรายการที่ได้รับไปแล้วจะคงฉบับของตนไว้ และการส่งจะรายงานว่าข้ามรายการนั้น",
  "chainMenu.result": "แต่ละสาขาได้รับอะไรบ้าง",
  "chainMenu.resultLine": "เพิ่ม {created} รายการ ปรับปรุง {updated} รายการ",
  "chainMenu.resultSkipped": "ข้าม {count} รายการ เพราะสาขาแก้ไขเอง",
  "chainUsers.title": "สิทธิ์เข้าถึงสาขา",
  "chainUsers.description":
    "บัญชีหลังร้านแต่ละบัญชีทำงานในสาขาใดได้บ้าง บัญชีใหม่เข้าถึงได้ทั้งเครือจนกว่าจะจำกัดที่นี่",
  "chainUsers.name": "ผู้ใช้",
  "chainUsers.username": "ชื่อผู้ใช้",
  "chainUsers.email": "อีเมล",
  "chainUsers.role": "บทบาท",
  "chainUsers.record": "ประวัติ",
  "chainUsers.reach": "เข้าถึง",
  "chainUsers.stores": "สาขา",
  "chainUsers.wholeChain": "ทั้งเครือ",
  "chainUsers.wholeChainHint": "ทุกสาขา รวมถึงสาขาที่เพิ่มภายหลัง",
  "chainUsers.namedStores": "เฉพาะสาขาเหล่านี้",
  "chainUsers.namedStoresHint": "เข้าสู่ระบบ สลับสาขา และดูรายงานได้เฉพาะสาขาเหล่านี้เท่านั้น",
  "chainUsers.storeCount": "{count} สาขา",
  "chainUsers.you": "คุณ",
  "capability.configure_coupons": "คูปองของสาขาเอง",
  "capability.configure_discount_promos": "โปรโมชันส่วนลดของสาขาเอง",
  "capability.configure_topup_tiers": "ระดับการเติมเงินของสาขาเอง",
  "capability.custom_topup_amounts": "กรอกยอดเติมเงินเอง",

  "settings.invoice.title": "การออกเลขที่ใบกำกับภาษี",
  "settings.invoice.description": "เลขที่ที่พิมพ์บนบิล และข้อมูลจดทะเบียนภาษีที่พิมพ์อยู่ด้านบน",
  "settings.invoice.numbering": "การออกเลขที่",
  "settings.invoice.enabled": "พิมพ์เลขที่ใบกำกับภาษีบนใบเสร็จ",
  "settings.invoice.prefix": "คำนำหน้า",
  "settings.invoice.dateType": "เริ่มนับลำดับใหม่",
  "settings.invoice.dateType.none": "ไม่เริ่มใหม่ — เรียงต่อเนื่องชุดเดียว",
  "settings.invoice.dateType.year": "ทุกปี",
  "settings.invoice.dateType.date": "ทุกวัน",

  "settings.invoice.nextNumber": "เลขที่ถัดไป",
  "settings.invoice.rangeHint": "ตลาดของคุณกำหนดไว้ที่ {min} ถึง {max}",
  "settings.invoice.preview": "ใบกำกับภาษีถัดไปจะเป็น",
  "settings.invoice.continuity":
    "เลขที่พิมพ์เรียงต่อเนื่องไม่ขาดตอน และเดินหน้าได้อย่างเดียว การเปลี่ยนมีผลกับออร์เดอร์ใหม่เท่านั้น เอกสารที่พิมพ์ไปแล้วจะไม่ถูกออกเลขใหม่",
  "settings.invoice.taxIdentity": "ข้อมูลภาษีบนใบเสร็จ",
  "settings.invoice.taxIdentityHint": "พิมพ์อยู่เหนือรายการ เมื่อแม่แบบใบเสร็จตั้งให้แสดง",
  "settings.invoice.companyName": "ชื่อกิจการ",
  "settings.invoice.registrationNumber": "เลขประจำตัวผู้เสียภาษี",
  "settings.invoice.companyAddress": "ที่อยู่กิจการ",
  "settings.invoice.deviceNumber": "เลขที่เครื่องที่จดทะเบียน",
  "settings.invoice.deviceNumberHint":
    "ในตลาดที่หน่วยงานภาษีจดทะเบียนตัวเครื่องด้วย เลขนี้ต้องอยู่บนใบกำกับภาษีทุกใบ หากของคุณไม่ต้องใช้ ให้เว้นว่างไว้",

  "tradingStatus.open": "เปิดทำการ",
  "tradingStatus.temporarily_closed": "ปิดชั่วคราว",
  "tradingStatus.permanently_closed": "ปิดถาวร",

  "nav.items": "รายการ",
  "nav.categories": "หมวดหมู่",
  "nav.specifications": "กลุ่มตัวเลือก",
  "nav.toppings": "ท็อปปิง",
  "nav.tags": "ป้ายกำกับ",
  "nav.tables": "โต๊ะ",
  "nav.settings": "ตั้งค่า",
  "nav.storeProfile": "ข้อมูลร้าน",
  "nav.tax": "ภาษี",
  "nav.serviceCharge": "ค่าบริการ",
  "nav.discountExclusions": "รายการที่ไม่ลดราคา",
  "nav.paymentMethods": "วิธีชำระเงิน",
  "nav.quickNotes": "โน้ตด่วน",

  "action.add": "เพิ่ม",
  "action.view": "ดู",
  "action.edit": "แก้ไข",
  "action.delete": "ลบ",
  "action.save": "บันทึก",
  "action.cancel": "ยกเลิก",
  "action.saving": "กำลังบันทึก…",
  "action.select": "เลือกหนึ่งรายการ",
  "action.search": "ค้นหา",
  "action.clear": "ล้าง",
  "action.previous": "ก่อนหน้า",
  "action.next": "ถัดไป",
  "action.moveUp": "เลื่อนขึ้น",
  "action.moveDown": "เลื่อนลง",
  "action.regenerate": "สร้างใหม่",
  "action.close": "ปิด",
  "action.confirmDelete": "ลบ",

  "list.empty": "ยังไม่มีข้อมูล",
  "list.noMatches": "ไม่พบรายการที่ตรงกับการค้นหา",
  "list.noFilterMatches": "ไม่พบรายการที่ตรงกับตัวกรอง",
  "list.showing": "{from}–{to} จาก {total}",
  "list.noResults": "ไม่พบผลลัพธ์",
  "list.pagination": "การแบ่งหน้า",
  "list.page": "หน้า {page}",
  "list.morePages": "หน้าเพิ่มเติม",
  "list.perPage": "แถวต่อหน้า",
  "list.perPageOption": "{count} รายการต่อหน้า",
  "list.sortAscending": "เรียงจากน้อยไปมาก",
  "list.sortDescending": "เรียงจากมากไปน้อย",
  "list.loadFailed": "โหลดรายการนี้ไม่สำเร็จ",
  "list.saveFailed": "บันทึกไม่สำเร็จ",

  "calendar.previousMonth": "เดือนก่อนหน้า",
  "calendar.nextMonth": "เดือนถัดไป",

  "dateRange.label": "ช่วงวันที่",
  "dateRange.apply": "ใช้",
  "dateRange.today": "วันนี้",
  "dateRange.yesterday": "เมื่อวาน",
  "dateRange.last7Days": "7 วันล่าสุด",
  "dateRange.last30Days": "30 วันล่าสุด",
  "dateRange.monthToDate": "เดือนนี้ถึงวันนี้",
  "dateRange.lastMonth": "เดือนก่อน",
  "dateRange.yearToDate": "ปีนี้ถึงวันนี้",
  "dateRange.lastYear": "ปีก่อน",

  "items.title": "รายการ",
  "items.description": "ทุกอย่างบนเมนู พร้อมราคา ตัวเลือก และจุดผลิตที่ส่งไป",
  "items.searchPlaceholder": "ค้นหาด้วยชื่อหรือรหัส",
  "items.new": "เพิ่มรายการ",
  "items.edit": "แก้ไขรายการ",
  "items.record": "ประวัติ",
  "items.money": "เงิน",
  "items.name": "ชื่อ",
  "items.code": "รหัสสินค้า",
  "items.codeHint": "รหัส SKU หรือ PLU ที่พิมพ์บนใบสั่งงานครัว",
  "items.price": "ราคา",
  "items.priceHint": "หน่วยเป็น {currency}",
  "items.category": "หมวดหมู่",
  "items.status": "สถานะ",
  "items.recipe": "ทำจากอะไรบ้าง",
  "items.recipeHint": "วัสดุที่ผูกไว้จะถูกตัดออกจากสต๊อกเมื่อชำระบิลแล้ว ไม่ใช่ตอนรับออร์เดอร์",
  "items.soldOut": "ของหมด",
  "items.autoSoldOut": "ให้สต๊อกเป็นตัวกำหนด",
  "items.autoSoldOutHint":
    "จะขึ้นว่าของหมดเองเมื่อวัสดุในสูตรหมด และกลับมาขายได้เมื่อสต๊อกกลับมา รายการที่ไม่มีสูตรจะไม่ถูกแตะต้อง",
  "items.available": "พร้อมขาย",
  "items.madeToOrder": "ทำเมื่อสั่ง",
  "items.stations": "จุดผลิต",
  "items.stationsHint": "จุดที่ทำรายการนี้ อาจมีมากกว่าหนึ่งจุด และแต่ละจุดจะได้ใบสั่งงานของตัวเอง",
  "items.stationsNone": "ไม่ระบุจุดผลิต",
  "items.stationPrinters": "เครื่องพิมพ์",
  "items.stationPrintersAll": "เครื่องพิมพ์ทั้งหมดของจุดผลิตนี้",
  "items.packingFee": "ค่าบรรจุภัณฑ์",
  "items.kind": "ประเภทรายการ",
  "items.kindHint": "จานเดี่ยว ชุดที่ประกอบจากจานอื่น รายการเฉพาะกิจ หรือรายการที่สั่งล่วงหน้า",
  "items.kind.normal": "จานปกติ",
  "items.kind.mealSet": "ชุดอาหาร",
  "items.kind.temporary": "รายการเฉพาะกิจ",
  "items.kind.preOrder": "สั่งล่วงหน้า",
  "items.pricingMethod": "ขายตาม",
  "items.pricing.unit": "จาน",
  "items.pricing.weight": "น้ำหนัก",
  "items.wasPrice": "ราคาเดิม",
  "items.deliveryPrice": "ราคาเดลิเวอรี",
  "items.deliveryPriceHint":
    "ราคาของรายการนี้บนแพลตฟอร์มเดลิเวอรี เว้นว่างไว้เพื่อขายตามราคาเมนู แพลตฟอร์มเก็บค่าคอมมิชชัน เมนูส่วนใหญ่จึงบวกราคาเพิ่มตรงนี้",
  "items.wasPriceHint": "แสดงขีดฆ่าไว้ข้างราคา เว้นว่างไว้หากไม่มีโปรโมชัน",
  "items.pricedAtTill": "กำหนดราคาที่เครื่องขายทุกวัน",
  "items.mealSet": "ในชุดมีอะไรบ้าง",
  "items.mealSetHint": "กลุ่มตัวเลือก แต่ละตัวเลือกคือจานอื่นบนเมนูนี้ คิดราคาตามส่วนต่างที่เพิ่ม",
  "items.mealSetGroup": "ตัวเลือก",
  "items.mealSetPicks": "เลือกได้",
  "items.mealSetPick": "เลือกจาน",
  "items.mealSetDelta": "เพิ่มเงิน",
  "items.mealSetAddOption": "เพิ่มจาน",
  "items.mealSetAddGroup": "เพิ่มกลุ่มตัวเลือก",
  "items.deleteTitle": "ลบรายการนี้หรือไม่",
  "items.deleteBody":
    "รายการจะหายไปจากเมนู ออร์เดอร์ที่ผ่านมายังเก็บบันทึกของตนเองไว้ ประวัติจึงไม่เปลี่ยนแปลง",
  "items.allCategories": "ทุกหมวดหมู่",
  "items.allStatuses": "ทุกสถานะ",
  "items.uncategorised": "ไม่มีหมวดหมู่",
  "items.nameInLocale": "ชื่อภาษา{locale}",
  "items.translationHint": "ลูกค้าที่สั่งเป็นภาษา{locale}จะเห็นข้อความนี้แทน",
  "items.modifiers": "ตัวเลือกเพิ่มเติม",
  "items.modifiersHint": "สิ่งที่แคชเชียร์จะถูกถามเมื่อกดเลือกรายการนี้",
  "items.modifiersNone": "ไม่มี",

  "categories.title": "หมวดหมู่",
  "categories.description": "เมนูถูกจัดกลุ่มอย่างไร และเรียงลำดับแบบใด",
  "categories.new": "เพิ่มหมวดหมู่",
  "categories.edit": "แก้ไขหมวดหมู่",
  "categories.record": "ประวัติ",
  "categories.name": "ชื่อ",
  "categories.itemCount": "รายการ",
  "categories.allStatuses": "ทุกสถานะ",
  "categories.deleteTitle": "ลบหมวดหมู่นี้หรือไม่",
  "categories.deleteBody": "หมวดหมู่ที่ยังมีรายการอยู่ลบไม่ได้ ให้ย้ายรายการออกก่อน",

  "modifiers.specifications": "กลุ่มตัวเลือก",
  "modifiers.specificationsDescription": "ตัวเลือกที่เปลี่ยนสิ่งที่ขาย เช่น ขนาด ร้อนหรือเย็น",
  "modifiers.toppings": "ท็อปปิง",
  "modifiers.toppingsDescription": "ของเพิ่มที่มีราคา ลูกค้าเลือกเพิ่มได้",
  "modifiers.tags": "ป้ายกำกับ",
  "modifiers.tagsDescription": "คำสั่งถึงจุดผลิต ใส่ราคาได้หากร้านคิดเงินเพิ่ม",
  "modifiers.record": "ประวัติ",
  "modifiers.options": "ตัวเลือก",
  "modifiers.optionName": "ชื่อ",
  "modifiers.optionPrice": "ส่วนต่างราคา",
  "modifiers.optionStatus": "สถานะ",
  "modifiers.addOption": "เพิ่มตัวเลือก",
  "modifiers.required": "ต้องเลือกอย่างน้อยหนึ่งอย่าง",
  "modifiers.selection": "การเลือก",
  "modifiers.single": "เลือกได้อย่างเดียว",
  "modifiers.multiple": "เลือกได้หลายอย่าง",
  "modifiers.optionDefault": "เลือกไว้ให้ล่วงหน้า",
  "modifiers.storeDefault": "เพิ่มให้ทุกรายการใหม่",
  "modifiers.usedBy": "ใช้โดย",
  "modifiers.deleteTitle": "ลบกลุ่มนี้หรือไม่",
  "modifiers.deleteBody":
    "รายการที่ผูกกับกลุ่มนี้จะไม่มีตัวเลือกเหล่านี้อีกต่อไป ออร์เดอร์ที่ผ่านมายังเก็บบันทึกสิ่งที่เลือกไว้ ประวัติจึงไม่เปลี่ยนแปลง",

  "tables.title": "โต๊ะ",
  "tables.description": "ผังร้าน และรหัส QR ประจำแต่ละโต๊ะ",
  "tables.new": "เพิ่มโต๊ะ",
  "tables.edit": "แก้ไขโต๊ะ",
  "tables.record": "ประวัติ",
  "tables.name": "โต๊ะ",
  "tables.area": "โซน",
  "tables.capacity": "จำนวนที่นั่ง",
  "tables.qrCode": "รหัส QR",
  "tables.status": "สถานะ",
  "tables.tabList": "รายชื่อโต๊ะ",
  "tables.tabFloorPlan": "ผังร้าน",
  "tables.viewsLabel": "มุมมองโต๊ะ",
  "tables.searchPlaceholder": "ค้นหาโต๊ะ…",
  "tables.count": "{count} โต๊ะ",
  "tables.qrOrdering": "สั่งผ่าน QR",
  "tables.viewQr": "ดูรหัส QR",
  "tables.qrExplainer": "ลูกค้าสแกนรหัส QR เพื่อดูเมนูและสั่งอาหารได้",
  "tables.qrUnavailable":
    "การสั่งผ่าน QR ยังไม่ได้ตั้งที่อยู่สาธารณะ จึงยังไม่มีอะไรให้โทรศัพท์สแกน",
  "tables.qrFor": "รหัส QR ของ{table}",
  "tables.printQr": "พิมพ์",
  "tables.deleteTitle": "ลบ{name}หรือไม่",
  "tables.deleteBody": "โต๊ะและรหัส QR ของโต๊ะจะหายไป ออร์เดอร์ที่ผ่านมายังเก็บบันทึกไว้",
  "tables.statusHint": "สถานะที่นั่งถูกกำหนดจากเครื่องขายเมื่อรับลูกค้าและให้บริการ",
  "tables.decreaseCovers": "ลดจำนวนที่นั่ง",
  "tables.increaseCovers": "เพิ่มจำนวนที่นั่ง",
  "tables.emptyTitle": "ยังไม่มีโต๊ะ",
  "tables.emptyBody": "สร้างโต๊ะแรกเพื่อเริ่มรับออร์เดอร์และสร้างรหัส QR ประจำโต๊ะ",
  "tables.floorPlanTitle": "ยังไม่ได้สร้างผังร้าน",
  "tables.floorPlanBody": "ขณะนี้จัดการโต๊ะและโซนได้จากรายชื่อโต๊ะก่อน",
  "tables.areas": "โซน",
  "tables.newArea": "เพิ่มโซน",
  "tables.defaultArea": "โซนเริ่มต้น",
  "tables.regenerateQrTitle": "สร้างรหัส QR ใหม่ให้โต๊ะนี้หรือไม่",
  "tables.regenerateQrBody":
    "รหัสที่พิมพ์ไว้เดิมของโต๊ะนี้จะใช้ไม่ได้ทันที ให้พิมพ์และติดรหัสใหม่ก่อนเปิดบริการรอบถัดไป",
  "tables.tempCode": "รหัสลูกค้าจร",
  "tables.tempCodeDescription":
    "หมายเลขสำหรับออร์เดอร์ที่ไม่มีโต๊ะ เช่น เคาน์เตอร์ บาร์ หรือคนขับส่งอาหาร",
  "tables.tempCodeEnabled": "ใช้รหัสลูกค้าจร",
  "tables.tempCodePrefix": "คำนำหน้า",
  "tables.tempCodeFrom": "ตั้งแต่",
  "tables.tempCodeTo": "ถึง",

  "settings.store.title": "ข้อมูลร้าน",
  "settings.store.description": "ร้านคือใคร อยู่ที่ไหน และวันขายสิ้นสุดเมื่อใด",
  "settings.store.name": "ชื่อร้าน",
  "settings.store.address": "ที่อยู่",
  "settings.store.phone": "โทรศัพท์",
  "settings.store.timezone": "เขตเวลา",
  "settings.store.businessDayCutoff": "วันขายสิ้นสุดเวลา",
  "settings.store.businessDayCutoffHint":
    "ยอดขายหลังเที่ยงคืนจะนับเป็นของวันก่อนหน้าจนถึงเวลานี้ วันของร้านอาหารจบเมื่อปิดบริการ",
  "settings.store.statisticsBasis": "รายงานนับยอดขายเป็นของวัน",
  "settings.store.statisticsBasisHint":
    "รายงานทุกฉบับจะนับยอดขายเป็นของวันใด คนละเรื่องกับเวลาสิ้นสุดวันขายด้านบน เวลานั้นกำหนดว่าบิลถูกประทับเป็นวันใด ส่วนค่านี้กำหนดว่ารายงานอ่านวันใดในสองวันที่ประทับไว้ ร้านจึงตั้งเวลาสิ้นสุดวันขายไว้ตีสามและยังปิดบัญชีตามเดือนปฏิทินได้",
  "settings.store.statisticsBasisBusinessDay": "วันขาย ซึ่งสิ้นสุดตามเวลาที่ตั้งไว้",
  "settings.store.statisticsBasisCalendarDay": "วันตามปฏิทิน ตั้งแต่เที่ยงคืนถึงเที่ยงคืน",
  "settings.store.languages": "ภาษา",
  "settings.store.languagesHint":
    "หน้าจอและใบเสร็จของร้านนี้รองรับภาษาใดบ้าง ใบเสร็จภาษาเขมรพิมพ์เป็นรูปภาพ เพราะเครื่องพิมพ์ความร้อนแสดงอักษรนี้จากรหัสหน้าไม่ได้",
  "settings.store.currency": "สกุลเงิน",
  "settings.store.primaryCurrency": "สกุลเงินหลัก",
  "settings.store.secondCurrency": "สกุลเงินที่สอง",
  "settings.store.exchangeRate": "อัตราแลกเปลี่ยน",
  "settings.store.exchangeRateHint":
    "{second} กี่หน่วยต่อ {primary} หนึ่งหน่วย การเปลี่ยนไม่มีผลกับบิลที่ชำระไปแล้ว",
  "settings.store.rateHistory": "ประวัติอัตราแลกเปลี่ยน",
  "settings.store.changedBy": "ผู้เปลี่ยน",
  "settings.store.effectiveFrom": "มีผลตั้งแต่",

  "settings.tax.title": "ภาษี",
  "settings.tax.description": "คำนวณภาษีบนบิลอย่างไร",
  "settings.tax.rules": "กฎภาษี",
  "settings.tax.unset": "กำหนดอัตราของคุณ",
  "settings.tax.newRule": "เพิ่มกฎ",
  "settings.tax.editRule": "แก้ไขกฎ",
  "settings.tax.code": "รหัส",
  "settings.tax.name": "ชื่อ",
  "settings.tax.rate": "อัตรา",
  "settings.tax.rateHint": "เป็นเปอร์เซ็นต์ เก็บทศนิยมสองตำแหน่งอย่างแม่นยำ",
  "settings.tax.inclusive": "รวมอยู่ในราคาแล้ว",
  "settings.tax.inclusiveHint": "ราคาบนเมนูรวมภาษีนี้ไว้แล้ว จะแสดงบนบิลแต่ไม่บวกเพิ่ม",
  "settings.tax.exclusiveHint": "ภาษีนี้บวกเพิ่มบนบิลที่เครื่องขาย",
  "settings.tax.sequence": "ลำดับ",
  "settings.tax.appliesToCategories": "เฉพาะหมวดหมู่เหล่านี้",
  "settings.tax.appliesToCategoriesHint": "เว้นว่างไว้เพื่อใช้กับทุกรายการ",
  "settings.tax.appliesToDiningStyles": "เฉพาะรูปแบบบริการเหล่านี้",
  "settings.tax.appliesToServiceCharge": "คิดภาษีกับค่าบริการด้วย",
  "settings.tax.appliesToTableFee": "คิดภาษีกับค่าโต๊ะด้วย",
  "settings.tax.chargeTax": "เก็บภาษี",
  "settings.tax.chargeTaxHint":
    "ปิดสวิตช์นี้เพื่อหยุดเก็บภาษีชั่วคราวโดยยังเก็บกฎไว้ ไม่เหมือนกับอัตราศูนย์ ซึ่งยังบันทึกบรรทัดภาษีอยู่",
  "settings.tax.label": "เรียกว่าอะไร",
  "settings.tax.labelHint": "VAT, GST, ภาษีขาย หรือคำที่ปรากฏบนใบเสร็จของคุณ",
  "settings.tax.deleteTitle": "ลบกฎภาษีนี้หรือไม่",
  "settings.tax.deleteBody": "จะไม่ใช้กับบิลใหม่อีก บิลที่ชำระไปแล้วยังคงอัตราที่คิดไว้เดิม",

  "settings.serviceCharge.title": "ค่าบริการ",
  "settings.serviceCharge.description": "ร้านคิดค่าบริการเท่าไร และเมื่อใด",
  "settings.serviceCharge.newRate": "เพิ่มอัตรา",
  "settings.serviceCharge.deleteTitle": "ลบอัตรานี้หรือไม่",
  "settings.serviceCharge.deleteBody":
    "จะไม่บวกกับบิลใหม่อีก บิลที่ชำระไปแล้วยังคงค่าบริการที่คิดไว้เดิม",
  "settings.serviceCharge.rate": "อัตรา",
  "settings.serviceCharge.default": "ค่าเริ่มต้น",
  "settings.serviceCharge.appliesTo": "ใช้กับ",
  "settings.serviceCharge.calculation": "วิธีคำนวณ",
  "settings.serviceCharge.onSubtotal": "จากยอดรวมทั้งบิล",
  "settings.serviceCharge.perUnit": "ต่อรายการ",
  "settings.serviceCharge.calculationHint":
    "แบบต่อรายการจะคิดและปัดเศษทีละหน่วย ซึ่งอาจต่างจากการคิดทั้งบิลอยู่ไม่กี่สตางค์",
  "settings.serviceCharge.rounding": "การปัดเศษ",
  "settings.serviceCharge.tableFee": "ค่าโต๊ะ",
  "settings.serviceCharge.tableFeeEnabled": "เก็บค่าโต๊ะ",
  "settings.serviceCharge.tableFeeBasis": "คิดแบบ",
  "settings.serviceCharge.perHead": "ต่อคน",
  "settings.serviceCharge.perTable": "ต่อโต๊ะ",
  "settings.serviceCharge.amount": "จำนวนเงิน",
  "settings.tip.title": "ทิป",
  "settings.tip.description":
    "ทิปคือเงินของลูกค้าที่ส่งต่อให้พนักงาน ส่วนค่าบริการเป็นของร้าน สองอย่างนี้ต่างกันตามกฎหมาย และจะใช้ทั้งคู่ก็ได้",
  "settings.tip.enabled": "เสนอให้ทิปที่เครื่องขาย",
  "settings.tip.suggested": "เปอร์เซ็นต์ที่แนะนำ",
  "settings.tip.suggestedHint": "ไม่เกินสี่ค่า คั่นด้วยจุลภาค ชุดที่ใช้กันทั่วไปคือ 18, 20, 22",
  "settings.tip.basis": "คิดเปอร์เซ็นต์จาก",
  "settings.tip.basisHint": "เครื่องรูดบัตรมักแนะนำจากยอดรวม ส่วนธรรมเนียมเดิมคิดจากยอดก่อนภาษี",
  "settings.tip.basisTotal": "ยอดรวมหลังภาษี",
  "settings.tip.basisSubtotal": "ยอดก่อนภาษี",

  "settings.discountExclusions.title": "รายการที่ไม่ลดราคา",
  "settings.discountExclusions.description": "รายการที่ส่วนลดทั้งบิลไม่แตะต้อง",
  "settings.discountExclusions.excluded": "ยกเว้น",
  "settings.discountExclusions.selected": "ยกเว้น {count} รายการ",

  "settings.paymentMethods.title": "วิธีชำระเงิน",
  "settings.paymentMethods.description": "เครื่องขายเสนอวิธีใดบ้าง และเรียงลำดับอย่างไร",
  "settings.paymentMethods.enabled": "เปิดใช้",
  "settings.paymentMethods.provider": "ผู้ให้บริการ",
  "settings.paymentMethods.noProvider": "ไม่มี",
  "settings.paymentMethods.add": "เพิ่มวิธีชำระเงิน",
  "settings.paymentMethods.newName": "ให้เครื่องขายเรียกว่าอะไร",
  "settings.paymentMethods.builtIn": "มีมาให้",
  "settings.paymentMethods.builtInHint": "วิธีที่มีมาให้เปลี่ยนชื่อหรือปิดได้ แต่ลบไม่ได้",
  "settings.paymentMethods.nameHint": "เปลี่ยนชื่อวิธีใดก็ได้ เครื่องขายและทุกรายงานจะเปลี่ยนตาม",
  "settings.paymentMethods.deleteConfirm":
    "ลบวิธีชำระเงินนี้หรือไม่ บิลที่ผ่านมายังคงชื่อที่ใช้ชำระไว้เดิม",

  "settings.quickNotes.title": "โน้ตด่วน",
  "settings.quickNotes.description": "ปุ่มลัดที่แคชเชียร์กดแทนการพิมพ์",
  "settings.quickNotes.new": "เพิ่มโน้ต",
  "settings.quickNotes.record": "ประวัติ",
  "settings.quickNotes.deleteTitle": "ลบโน้ตนี้หรือไม่",
  "settings.quickNotes.deleteBody":
    "แคชเชียร์จะไม่เห็นโน้ตนี้อีก ออร์เดอร์ที่ใส่ไว้แล้วยังคงข้อความเดิม",

  "diningStyle.dine_in": "ทานที่ร้าน",
  "diningStyle.take_away": "สั่งกลับบ้าน",
  "diningStyle.pre_order": "สั่งล่วงหน้า",
  "diningStyle.delivery": "เดลิเวอรี",

  "status.enabled": "ใช้งาน",
  "status.disabled": "ซ่อน",
  "tableStatus.free": "ว่าง",
  "tableStatus.seated": "นั่งแล้ว",
  "tableStatus.ordered": "สั่งแล้ว",
  "tableStatus.bill_requested": "เรียกเก็บเงิน",

  "rounding.half_up": "ครึ่งขึ้น",
  "rounding.half_even": "ครึ่งไปเลขคู่",
  "rounding.half_down": "ครึ่งลง",
  "rounding.up": "ขึ้นเสมอ",
  "rounding.down": "ลงเสมอ",
  "rounding.ceiling": "ปัดขึ้น",
  "rounding.floor": "ปัดลง",

  "inventory.supplies.title": "วัสดุ",
  "inventory.supplies.description": "สิ่งที่จุดผลิตซื้อ เก็บ และใช้ไป",
  "inventory.supplies.new": "เพิ่มวัสดุ",
  "inventory.supplies.deleteTitle": "ลบวัสดุนี้หรือไม่",
  "inventory.supplies.deleteBody":
    "วัสดุที่ยังมีสต๊อกคงเหลือไม่สามารถลบได้ กรุณาตัดสต๊อกออกก่อน " +
    "ล็อตและรายการเคลื่อนไหวทั้งหมดยังคงอยู่",
  "inventory.number": "รหัส",
  "inventory.record": "ข้อมูล",
  "inventory.name": "ชื่อ",
  "inventory.otherName": "ชื่อเรียกอื่น",
  "inventory.category": "หมวดหมู่",
  "inventory.brand": "แบรนด์",
  "inventory.barcode": "บาร์โค้ด",
  "inventory.barcodeHint": "พิมพ์หรือสแกนก็ได้ หนึ่งบาร์โค้ดต่อหนึ่งวัสดุ",
  "inventory.stockUnit": "หน่วยนับสต๊อก",
  "inventory.stockUnitHint": "นับเป็นหน่วยใด เปลี่ยนไม่ได้เมื่อมีสต๊อกแล้ว",
  "inventory.purchaseUnit": "หน่วยสั่งซื้อ",
  "inventory.salesUnit": "หน่วยขาย",
  "inventory.minimumStock": "เตือนเมื่อต่ำกว่า",
  "inventory.inStock": "คงเหลือ",
  "inventory.belowMinimum": "ใกล้หมด",
  "inventory.suppliers.title": "ผู้จัดจำหน่าย",
  "inventory.suppliers.description": "ร้านซื้อจากใคร และคิดราคาเท่าไร",
  "inventory.suppliers.new": "เพิ่มผู้จัดจำหน่าย",
  "inventory.contactPerson": "ผู้ติดต่อ",
  "inventory.contactNumber": "โทรศัพท์",
  "inventory.address": "ที่อยู่",
  "inventory.nickname": "ชื่อย่อ",
  "inventory.supplierPrices": "วัสดุที่จัดส่ง",
  "inventory.warehouses.title": "คลังสินค้า",
  "inventory.warehouses.description": "สต๊อกเก็บอยู่ที่ใด และล็อตไหนออกก่อน",
  "inventory.warehouses.new": "เพิ่มคลังสินค้า",
  "inventory.isDefault": "ตัดยอดขายจากคลังนี้",
  "inventory.valuationPolicy": "ล็อตไหนออกก่อน",
  "inventory.valuation.fifo": "เข้าก่อนออกก่อน (FIFO)",
  "inventory.valuation.lifo": "เข้าหลังออกก่อน (LIFO)",
  "inventory.valuationHint": "ค่านี้กำหนดต้นทุนของการเบิกออก ไม่ใช่ลำดับที่แสดงในรายการ",
  "inventory.units.title": "หน่วยนับ",
  "inventory.units.description": "วัสดุวัดด้วยหน่วยใด และแปลงจากหน่วยหนึ่งเป็นอีกหน่วยอย่างไร",
  "inventory.units.new": "เพิ่มหน่วยนับ",
  "inventory.dimension": "วัดเป็น",
  "inventory.dimension.mass": "น้ำหนัก",
  "inventory.dimension.volume": "ปริมาตร",
  "inventory.dimension.count": "จำนวนชิ้น",
  "inventory.seeded": "มีมาให้",
  "inventory.conversions": "การแปลงหน่วย",
  "inventory.conversions.new": "เพิ่มการแปลงหน่วย",
  "inventory.conversionFrom": "หนึ่ง",
  "inventory.conversionTo": "เท่ากับ",
  "inventory.factor": "จำนวน",
  "inventory.appliesTo": "ใช้กับ",
  "inventory.everySupply": "วัสดุทุกชนิด",
  "inventory.categories.title": "หมวดวัสดุ",
  "inventory.categories.description": "สองระดับ คือหมวดหมู่ และหมวดย่อยที่อยู่ใต้หมวดนั้น",
  "inventory.categories.new": "เพิ่มหมวดหมู่",
  "inventory.categories.deleteTitle": "ลบหมวดหมู่นี้หรือไม่",
  "inventory.categories.deleteBody":
    "หมวดหมู่ที่มีวัสดุหรือหมวดหมู่ย่อยอยู่ไม่สามารถลบได้ กรุณาย้ายออกก่อน",
  "inventory.parent": "อยู่ใต้",
  "inventory.topLevel": "ไม่มี — เป็นระดับบนสุด",
  "inventory.subCategories": "หมวดย่อย",
  "inventory.supplyCount": "วัสดุ",
  "inventory.brands.title": "แบรนด์",
  "inventory.brands.description": "สินค้าของใคร ในกรณีที่เรื่องนี้สำคัญ",
  "inventory.brands.new": "เพิ่มแบรนด์",
  "inventory.brands.deleteTitle": "ลบแบรนด์นี้หรือไม่",
  "inventory.brands.deleteBody":
    "แบรนด์ที่ยังอยู่บนวัสดุไม่สามารถลบได้ กรุณานำออกจากวัสดุเหล่านั้นก่อน",
  "inventory.description": "หมายเหตุ",

  "nav.stock": "สต๊อก",
  "nav.purchases": "การสั่งซื้อ",
  "nav.inbound": "รับเข้า",
  "nav.outbound": "เบิกออก",
  "nav.stockCounts": "การตรวจนับ",
  "stock.title": "สต๊อก",
  "stock.description": "มีอะไรอยู่เท่าไร มูลค่าเท่าใด และอะไรกำลังจะหมด",
  "stock.warehouse": "คลังสินค้า",
  "stock.quantity": "คงเหลือ",
  "stock.value": "มูลค่า",
  "stock.onlyLow": "เฉพาะที่ใกล้หมด",
  "stock.batches": "ล็อต",
  "stock.usedIn": "ใช้ใน",
  "stock.batchNo": "เลขล็อต",
  "stock.received": "รับเข้า",
  "stock.remaining": "คงเหลือ",
  "stock.unitCost": "ต้นทุนต่อหน่วย",
  "stock.receivedAt": "รับเข้าเมื่อ",
  "stock.expiresAt": "ควรใช้ก่อน",
  "stock.drawOrder": "เบิกตามลำดับนี้",
  "stock.drawOrderReverse": "เบิกย้อนลำดับนี้",
  "purchases.title": "การสั่งซื้อ",
  "purchases.description": "สั่งอะไรไปแล้วบ้าง และอะไรมาถึงแล้ว",
  "purchases.new": "สร้างใบสั่งซื้อ",
  "purchases.type": "ซื้อจาก",
  "purchases.type.supplier": "ผู้จัดจำหน่าย",
  "purchases.type.market": "ตลาด",
  "purchases.type.head_office": "สำนักงานใหญ่",
  "purchases.supplier": "ผู้จัดจำหน่าย",
  "purchases.total": "ยอดรวม",
  "purchases.notifyInbound": "ส่งไปยังคลังสินค้า",
  "purchases.receiveNow": "รับเข้าทันที",
  "purchases.void": "ยกเลิก",
  "purchases.voidReason": "เหตุผล",
  "inbound.title": "รับเข้า",
  "inbound.description": "ของที่รอตรวจรับ และของที่รับเข้าแล้ว",
  "inbound.new": "สร้างใบรับเข้า",
  "inbound.type": "เหตุผล",
  "inbound.type.supplier": "จากผู้จัดจำหน่าย",
  "inbound.type.market": "จากตลาด",
  "inbound.type.item_return": "ส่งคืน",
  "inbound.type.overstock": "พบเกินในสต๊อก",
  "inbound.type.transfer": "โอนเข้า",
  "inbound.approve": "รับเข้า",
  "inbound.reject": "ปฏิเสธ",
  "inbound.rejectReason": "เหตุผล",
  "inbound.batchNo": "เลขล็อต",
  "inbound.unitCost": "ต้นทุนต่อหน่วย",
  "inbound.freeQuantity": "ของแถม",
  "outbound.title": "เบิกออก",
  "outbound.description": "อะไรออกไปบ้าง เพราะอะไร และต้นทุนเท่าไร",
  "outbound.new": "สร้างใบเบิกออก",
  "outbound.type": "เหตุผล",
  "outbound.type.loss": "สูญเสีย",
  "outbound.type.sales": "ขายต่อ",
  "outbound.type.transfer": "โอนออก",
  "outbound.type.item_sales": "ขายเป็นอาหาร",
  "outbound.type.shortage": "ขาดจำนวน",
  "outbound.destination": "ไปยังคลัง",
  "outbound.cost": "ต้นทุน",
  "outbound.shortfall": "ขาดไป",
  "outbound.customer": "ลูกค้า",
  "outbound.salesAmount": "ขายได้",
  "counts.title": "การตรวจนับ",
  "counts.description": "ของบนชั้นบอกอย่างไร เทียบกับที่บัญชีบอก",
  "counts.new": "สร้างใบตรวจนับ",
  "counts.scope": "ตรวจนับ",
  "counts.scope.all": "ทั้งหมด",
  "counts.scope.picked": "เฉพาะที่เลือก",
  "counts.counter": "ผู้ตรวจนับ",
  "counts.expected": "บัญชีบอกว่า",
  "counts.counted": "นับได้จริง",
  "counts.difference": "ผลต่าง",
  "counts.record": "บันทึกผลนับ",
  "counts.adjust": "ลงบัญชีผลต่าง",
  "counts.adjustHint": "การนี้เคลื่อนย้ายสต๊อกโดยไม่มีการรับของรองรับ จึงบันทึกไว้ในชื่อของคุณ",
  "document.number": "เลขที่",
  "document.status": "สถานะ",
  "document.date": "วันที่ออก",
  "document.lines": "รายการ",
  "document.addLine": "เพิ่มรายการ",
  "document.quantity": "จำนวน",
  "document.unitPrice": "ราคาต่อหน่วย",
  "document.remarks": "หมายเหตุ",
  "status.purchasing": "กำลังสั่งซื้อ",
  "status.head_office_reviewing": "รอสำนักงานใหญ่",
  "status.head_office_rejected": "สำนักงานใหญ่ไม่อนุมัติ",
  "status.pending_inbound": "รอรับเข้า",
  "status.ended": "เสร็จสิ้น",
  "status.voided": "ยกเลิกแล้ว",
  "status.draft": "ฉบับร่าง",
  "status.waiting_review": "รอตรวจสอบ",
  "status.approving": "กำลังตรวจสอบ",
  "status.received": "รับเข้าแล้ว",
  "status.rejected": "ถูกปฏิเสธ",
  "status.issued": "จ่ายออกแล้ว",
  "status.transferring": "อยู่ระหว่างโอน",
  "status.blocked": "สต๊อกไม่พอ",
  "status.counting": "กำลังนับ",
  "status.adjusted": "ลงบัญชีแล้ว",

  "nav.ordersSection": "ออร์เดอร์",
  "nav.orderList": "ออร์เดอร์ทั้งหมด",
  "nav.reports": "รายงาน",
  "nav.costs": "ค่าใช้จ่ายร้าน",
  "nav.deliveryProviders": "แพลตฟอร์มเดลิเวอรี",
  "nav.billing": "แพ็กเกจ",
  "nav.campaigns": "แคมเปญ",
  "billing.title": "แพ็กเกจของคุณ",
  "billing.description": "แพ็กเกจที่ใช้อยู่ ครอบคลุมอะไรบ้าง และชำระไปแล้วเท่าไร",
  "billing.plan": "แพ็กเกจ",
  "billing.perMonth": "{amount} ต่อเดือน",
  "billing.permanent": "แพ็กเกจนี้ไม่มีวันหมดอายุ",
  "billing.expires": "ใช้ได้ถึง {date} — เหลืออีก {days} วัน",
  "billing.lapsed": "หมดอายุเมื่อ {date}",
  "billing.allowances": "สิ่งที่ครอบคลุม",
  "billing.unmetered": "{used} — ไม่จำกัด",
  "billing.renew": "ต่ออายุ",
  "billing.renewHint": "ต่ออายุแพ็กเกจที่ใช้อยู่ การเปลี่ยนแพ็กเกจต้องทำสัญญาใหม่ กรุณาติดต่อเรา",
  "billing.months": "จำนวนเดือน",
  "billing.cycle": "รอบชำระ",
  "billing.monthly": "รายเดือน",
  "billing.yearly": "รายปี",
  "billing.payments": "การชำระเงิน",
  "billing.noPayments": "ยังไม่มีการเรียกเก็บเงิน",
  "subscriptionStatus.normal": "ใช้งานอยู่",
  "subscriptionStatus.expired": "หมดอายุ",
  "subscriptionStatus.invalidated": "ระงับชั่วคราว",
  "subscriptionStatus.cancelled": "ยกเลิกแล้ว",
  "subscriptionStatus.trialing": "ทดลองใช้",
  "quota.item": "รายการ",
  "quota.employee": "พนักงาน",
  "quota.table": "โต๊ะ",
  "quota.customer": "ลูกค้า",
  "quota.supplier": "ผู้จัดจำหน่าย",
  "quota.store": "สาขา",
  "campaigns.title": "แคมเปญ",
  "campaigns.description": "ลดราคาชิ้นที่กำหนด คิดให้บนบิลอัตโนมัติ ไม่ต้องกรอกเอง",
  "campaigns.add": "เพิ่มแคมเปญ",
  "campaigns.name": "ชื่อ",
  "campaigns.kind": "ข้อเสนอ",
  "campaigns.kind.half_price_second": "ชิ้นที่สองลดครึ่งราคา",
  "campaigns.kind.buy_one_free_one": "ซื้อหนึ่งแถมหนึ่ง",
  "campaigns.kind.nth_item": "ลดราคาชิ้นที่กำหนด",
  "campaigns.nthItem": "ชิ้นที่เท่าไร",
  "campaigns.discount": "ลดเท่าไร",
  "campaigns.discountHint": "เป็นเปอร์เซ็นต์ ใส่ 100 หมายถึงฟรี",
  "campaigns.consecutive": "ใช้ซ้ำทั้งตะกร้า",
  "campaigns.consecutiveHint": "เปิด: สี่ชิ้นได้ข้อเสนอสองชิ้น ปิด: ใช้ได้ครั้งเดียวต่อบิล",
  "campaigns.active": "กำลังใช้งาน",
  "campaigns.reads": "ทุกชิ้นที่ {nth} ลด {percent}%",
  "campaigns.deleteTitle": "ลบแคมเปญนี้หรือไม่",
  "campaigns.deleteBody":
    "แคมเปญจะหายไปถาวร ไม่ใช่การหยุดชั่วคราว การปิดใช้งานจะหยุดข้อเสนอและเก็บแคมเปญไว้ใช้ภายหลัง บิลที่คิดราคาไปแล้วไม่เปลี่ยนแปลง",
  "campaigns.record": "ประวัติ",
  "campaigns.label": "ป้ายบนบิล",
  "campaigns.items": "สินค้าที่ร่วมรายการ",
  "campaigns.itemsAll": "ทั้งเมนู",
  "campaigns.window": "ช่วงที่ใช้ได้",
  "campaigns.validFrom": "เริ่ม",
  "campaigns.validTo": "ถึง",
  "nav.exports": "การส่งออกข้อมูล",
  "nav.chainOrders": "ออร์เดอร์ทั้งเครือ",
  "nav.insights": "ข้อมูลเชิงลึกธุรกิจ",
  "nav.chainReports": "รายงานทั้งเครือ",
  "orders.title": "ออร์เดอร์",
  "orders.description": "ทุกบิล มีอะไรอยู่บนบิล และเกิดอะไรขึ้นกับบิลนั้น",
  "orders.number": "เลขที่ออร์เดอร์",
  "orders.table": "โต๊ะ",
  "orders.covers": "จำนวนผู้ทาน",
  "orders.total": "ยอดรวม",
  "orders.paid": "ชำระแล้ว",
  "orders.status": "สถานะ",
  "orders.date": "รับออร์เดอร์เมื่อ",
  "orders.diningStyle": "รูปแบบบริการ",
  "orders.employee": "ผู้รับออร์เดอร์",
  "orders.refund": "คืนเงิน",
  "orders.tip": "ทิป",
  "orders.adjustTip": "แก้ไขทิป",
  "orders.adjustTipTitle": "แก้ไขทิปของการชำระรายการนี้",
  "orders.adjustTipHint": "กรอกยอดทิปที่ถูกต้อง ไม่ใช่ส่วนต่าง ทำได้จนกว่าวันขายนี้จะถูกปิดยอด",
  "orders.tipAdjusted": "แก้ไขทิปแล้ว",
  "orders.refundTitle": "คืนเงินบิลนี้",
  "orders.refundAmount": "จำนวนเงิน",
  "orders.refundAmountHint": "เว้นว่างไว้เพื่อคืนเงินทั้งหมดที่ยังรับไว้",
  "orders.refundReason": "เหตุผล",
  "orders.refundable": "คืนได้อีก",
  "orders.refunded": "คืนเงินแล้ว",
  "orders.reverse": "ย้อนการชำระ",
  "orders.reverseTitle": "ย้อนการชำระเงินของบิลนี้",
  "orders.reverseWarning":
    "บิลจะกลับไปเป็นบิลที่เปิดอยู่ และเงินที่รับไว้จะถูกคืน โต๊ะจะกลับมามีลูกค้าอีกครั้ง",
  "orders.reversed": "ย้อนการชำระแล้ว",
  "orders.items": "รายการ",
  "orders.taxLines": "ภาษี",
  "orders.payments": "การชำระเงิน",
  "orders.history": "ประวัติ",
  "orders.reprint": "พิมพ์ใบเสร็จอีกครั้ง",
  "orders.reprinted": "เข้าคิวพิมพ์แล้ว",
  "orders.allStatuses": "ทุกสถานะ",
  "orders.allStyles": "ทุกรูปแบบบริการ",
  "receipt.taxIncluded": "รวมแล้ว",
  "orders.inclusiveNote": "รวมอยู่ในราคาแล้ว",
  "payStatus.unpaid": "ยังไม่ชำระ",
  "payStatus.partly_paid": "ชำระบางส่วน",
  "payStatus.paid": "ชำระแล้ว",
  "payStatus.credit": "ค้างชำระ",
  "payStatus.refunded": "คืนเงินแล้ว",
  "payStatus.cancelled": "ยกเลิกแล้ว",
  "orderItem.ordered": "สั่งแล้ว",
  "orderItem.fired": "ส่งจุดผลิตแล้ว",
  "orderItem.served": "เสิร์ฟแล้ว",
  "orderItem.returned": "ส่งคืน",
  "orderItem.voided": "ยกเลิกรายการ",
  "event.created": "รับออร์เดอร์",
  "event.accepted": "ยืนยันออร์เดอร์",
  "event.item_added": "สั่งเพิ่ม",
  "event.item_returned": "ส่งคืนรายการ",
  "event.item_voided": "ยกเลิกรายการ",
  "event.item_fired": "ส่งไปจุดผลิต",
  "event.table_changed": "เปลี่ยนโต๊ะ",
  "event.covers_changed": "เปลี่ยนจำนวนผู้ทาน",
  "event.held": "พักบิล",
  "event.resumed": "เรียกบิลคืน",
  "event.merged": "รวมบิล",
  "event.split": "แยกบิล",
  "event.discounted": "ให้ส่วนลด",
  "event.tax_exempted": "ยกเว้นภาษี",
  "event.tax_reinstated": "คืนการคิดภาษี",
  "event.tip_adjusted": "แก้ไขทิป",
  "event.paid": "ชำระเงิน",
  "event.refunded": "คืนเงิน",
  "event.reversed": "ย้อนการชำระ",
  "event.cancelled": "ยกเลิก",
  "event.member_attached": "ระบุตัวสมาชิก",
  "event.points_earned": "ได้รับคะแนน",
  "event.supplies_deducted": "ตัดสต๊อก",
  "creditStatus.open": "ค้างอยู่",
  "creditStatus.settled": "ชำระแล้ว",
  "creditStatus.written_off": "ตัดเป็นหนี้สูญ",
  "insights.title": "ข้อมูลเชิงลึกธุรกิจ",
  "insights.description": "เครือร้านเป็นอย่างไร และอะไรดูผิดปกติ",
  "insights.overview": "ภาพรวม",
  "insights.trend": "แนวโน้ม",
  "insights.priceDistribution": "การกระจายของราคา",
  "insights.anomalies": "สิ่งที่ดูผิดปกติ",
  "insights.rules": "เกณฑ์",
  "insights.storeCount": "สาขา",
  "insights.allStores": "ทุกสาขา",
  "insights.comparedWith": "เทียบกับ {from} ถึง {to}",
  "insights.metric": "ตัวชี้วัด",
  "insights.metric.net": "ยอดขายสุทธิ",
  "insights.metric.orders": "จำนวนออร์เดอร์",
  "insights.metric.covers": "จำนวนผู้ทาน",
  "insights.metric.discount": "ส่วนลด",
  "insights.metric.perCover": "ยอดใช้จ่ายต่อคน",
  "insights.figure.net": "ยอดขายสุทธิ",
  "insights.figure.gross": "ยอดขายรวม",
  "insights.figure.discount": "ส่วนลด",
  "insights.figure.refunded": "การคืนเงิน",
  "insights.figure.orders": "จำนวนออร์เดอร์",
  "insights.figure.covers": "จำนวนผู้ทาน",
  "insights.column.store": "สาขา",
  "insights.column.item": "รายการ",
  "insights.column.lowest": "ถูกที่สุดที่",
  "insights.column.highest": "แพงที่สุดที่",
  "insights.column.spread": "ช่วงห่าง",
  "insights.column.median": "ค่ามัธยฐาน",
  "insights.column.stores": "สาขา",
  "insights.column.perCover": "ต่อคน",
  "insights.column.rule": "กฎ",
  "insights.priceScope":
    "ราคาเมนูปัจจุบันของทุกสาขา ไม่ว่าจะเลือกสาขาใดด้านบน ช่วงวันที่ไม่มีผล เพราะไม่มีประวัติราคาย้อนหลังให้อ่าน",
  "insights.noSpread": "ไม่มีรายการใดตั้งราคาต่างกันระหว่างสาขา",
  "insights.priceTruncated": "กำลังแสดง {shown} จาก {total} รายการที่มีช่วงห่างราคามากที่สุด",
  "insights.column.reading": "ค่าที่วัดได้",
  "insights.column.threshold": "เกณฑ์",
  "insights.column.evidence": "หลักฐาน",
  "insights.showEvidence": "ดูรายละเอียด",
  "insights.rule.discountRate": "อัตราส่วนลด",
  "insights.rule.voidRate": "อัตรายกเลิกรายการ",
  "insights.rule.refundRate": "อัตราคืนเงิน",
  "insights.rule.coverSpend": "ยอดใช้จ่ายต่อคน",
  "insights.direction.above": "สูงกว่า",
  "insights.direction.below": "ต่ำกว่าค่ามัธยฐานของเครือ",
  "insights.clear": "ไม่มีรายการใดเกินเกณฑ์ในช่วงเวลานี้",
  "insights.enabled": "เฝ้าดูอยู่",
  "insights.saved": "บันทึกแล้ว",
  "chainOrders.title": "ออร์เดอร์ทั้งเครือ",
  "chainOrders.description": "ทุกออร์เดอร์ที่รับทั่วทั้งเครือ",
  "chainOrders.store": "สาขา",
  "chainOrders.record": "ประวัติ",
  "nav.members": "สมาชิก",
  "nav.memberList": "รายชื่อลูกค้า",
  "nav.memberBalance": "ยอดเงินในร้าน",
  "nav.loyalty": "สะสมแต้ม",
  "members.title": "สมาชิก",
  "members.description": "ใครกลับมาอีก ถืออะไรอยู่ และสะสมอะไรไว้บ้าง",
  "members.number": "เลขสมาชิก",
  "members.name": "ชื่อ",
  "members.phone": "โทรศัพท์",
  "members.balance": "ยอดคงเหลือ",
  "members.points": "คะแนน",
  "members.tier": "ระดับ",
  "members.joined": "สมัครเมื่อ",
  "members.enrol": "สมัครสมาชิก",
  "members.classification": "เป็นสมาชิกของ",
  "members.anyClassification": "ที่ใดก็ได้ในเครือ",
  "members.classification.our_store": "สาขานี้",
  "members.classification.chain_store": "สาขาอื่น",
  "members.classification.non_chain": "นอกเครือ",
  "members.record": "ประวัติ",
  "members.registeredAt": "สมัครที่",
  "members.sharedWith": "ใช้ยอดร่วมกับ",
  "members.noStoredValue": "ไม่รวมอยู่ในแพ็กเกจนี้",
  "members.topUp": "เติมเงิน",
  "members.adjust": "ปรับยอดคงเหลือ",
  "members.setPin": "ตั้งรหัส PIN",
  "members.pin": "รหัส PIN",
  "members.pinSet": "ตั้งแล้ว",
  "members.pinUnset": "ยังไม่ได้ตั้ง",
  "members.amount": "จำนวนเงิน",
  "members.topUpHint": "โบนัสของระดับที่เข้าเงื่อนไขจะถูกเพิ่มให้อัตโนมัติ",
  "members.adjustPoints": "ปรับคะแนน",
  "members.balanceHistory": "ประวัติยอดคงเหลือ",
  "members.pointsHistory": "ประวัติคะแนน",
  "members.noMovements": "ยังไม่มีการเคลื่อนไหวของเงิน",
  "members.noPoints": "ยังไม่มีคะแนน",
  "members.movement": "การเคลื่อนไหว",
  "members.balanceAfter": "ยอดคงเหลือหลังรายการ",
  "members.whereStore": "ที่สาขา",
  "members.movement.topup": "เติมเงิน",
  "members.movement.spend": "ใช้จ่าย",
  "members.movement.refund": "คืนเงิน",
  "members.movement.adjust": "ปรับปรุง",
  "members.points.earn": "ได้รับ",
  "members.points.redeem": "แลกใช้",
  "members.points.adjust": "ปรับปรุง",
  "members.points.expire": "หมดอายุ",
  "balance.title": "ยอดเงินในร้าน",
  "balance.description": "ร้านค้างสมาชิกอยู่เท่าไร และสาขาใดเป็นผู้รับเงินไว้",
  "balance.liability": "ค้างสมาชิกอยู่",
  "balance.holders": "สมาชิกที่มียอดคงเหลือ",
  "balance.byStore": "แยกตามสาขา",
  "balance.toppedUp": "รับเข้า",
  "balance.spent": "ให้บริการแล้ว",
  "balance.held": "ยังถืออยู่",
  "balance.share": "สัดส่วน",
  "loyalty.title": "สะสมแต้ม",
  "loyalty.description": "โบนัสเติมเงิน และบิลหนึ่งได้แต้มเท่าไร",
  "loyalty.tiers": "ระดับการเติมเงิน",
  "loyalty.noTiers": "ยังไม่มีระดับ การเติมเงินจึงไม่ได้โบนัส",
  "loyalty.tierReads": "จ่าย {pay} ได้รับ {get}",
  "loyalty.pay": "จ่าย",
  "loyalty.bonus": "โบนัส",
  "loyalty.points": "คะแนน",
  "loyalty.pointsEnabled": "ให้คะแนนเมื่อชำระบิลแล้ว",
  "loyalty.perPoint": "ยอดใช้จ่ายต่อหนึ่งคะแนน",
  "loyalty.pointWorth": "หนึ่งคะแนนมีมูลค่า",
  "loyalty.minimumRedeemable": "คะแนนขั้นต่ำที่แลกได้",
  "action.yes": "ใช่",
  "action.no": "ไม่ใช่",
  "weekday.0": "อา.",
  "weekday.1": "จ.",
  "weekday.2": "อ.",
  "weekday.3": "พ.",
  "weekday.4": "พฤ.",
  "weekday.5": "ศ.",
  "weekday.6": "ส.",
  "nav.marketing": "การตลาด",
  "nav.coupons": "คูปอง",
  "nav.promos": "โปรโมชัน",
  "coupons.title": "คูปอง",
  "coupons.description": "ร้านแจกอะไร ให้ใคร และมีต้นทุนเท่าไร",
  "coupons.name": "คูปอง",
  "coupons.worth": "มูลค่า",
  "coupons.minSpend": "ยอดซื้อขั้นต่ำ",
  "coupons.issued": "ออกแล้ว",
  "coupons.used": "ใช้แล้ว",
  "coupons.given": "แจกไปแล้ว",
  "coupons.status": "สถานะ",
  "coupons.live": "ใช้งานอยู่",
  "coupons.off": "ปิดอยู่",
  "coupons.design": "ออกแบบคูปอง",
  "coupons.copy": "คัดลอก",
  "coupons.send": "ส่ง",
  "coupons.switchOn": "เปิดใช้",
  "coupons.switchOff": "ปิด",
  "coupons.kind": "ประเภท",
  "coupons.kind.amount": "ลดเป็นจำนวนเงิน",
  "coupons.kind.percent": "ลดเป็นเปอร์เซ็นต์",
  "coupons.amountOff": "ลดเป็นจำนวนเงิน",
  "coupons.percentOff": "ลดเป็นเปอร์เซ็นต์ หน่วยเบสิสพอยต์",
  "coupons.maxDiscount": "ลดได้ไม่เกิน",
  "coupons.maxDiscountHint": "เว้นว่างไว้หากไม่จำกัดเพดาน",
  "coupons.record": "ประวัติ",
  "coupons.limits": "จำนวนที่แจก",
  "coupons.issueLimit": "ทั้งหมด",
  "coupons.perMemberLimit": "ต่อสมาชิก",
  "coupons.window": "ช่วงที่ใช้ได้",
  "coupons.validFrom": "เริ่ม",
  "coupons.validTo": "ถึง",
  "coupons.performance": "ผลที่ได้",
  "promos.title": "โปรโมชัน",
  "promos.description": "ซื้อครบลดทันที เรียงตามลำดับที่พิจารณา",
  "promos.name": "โปรโมชัน",
  "promos.offer": "ข้อเสนอ",
  "promos.when": "เมื่อ",
  "promos.stacks": "ใช้ร่วมกับโปรอื่นได้",
  "promos.add": "เพิ่มโปรโมชัน",
  "promos.reads": "ซื้อครบ {threshold} ลด {reduction}",
  "promos.threshold": "ซื้อครบ",
  "promos.reduction": "ลด",
  "promos.days": "วัน",
  "promos.everyDay": "ทุกวัน",
  "promos.deleteTitle": "ลบโปรโมชันนี้หรือไม่",
  "promos.deleteBody":
    "โปรโมชันจะหายไปถาวร ไม่ใช่การหยุดชั่วคราว การปิดใช้งานจะหยุดข้อเสนอและเก็บโปรโมชันไว้ใช้ภายหลัง บิลที่คิดราคาไปแล้วไม่เปลี่ยนแปลง",
  "promos.record": "ประวัติ",
  "promos.order": "ลำดับที่พิจารณา",
  "promos.hours": "เวลา",
  "promos.allDay": "ทั้งวัน",
  "promos.validFrom": "เริ่ม",
  "promos.validTo": "ถึง",
  "pos.offline.ordering": "การรับออร์เดอร์",
  "pos.offline.payment": "เงินสดและการชำระแบบออฟไลน์",
  "pos.offline.printing": "การพิมพ์ใบเสร็จ",
  "pos.offline.noIncomingOrders": "ออร์เดอร์จากเครื่องอื่นจะไม่เข้ามาที่เครื่องนี้",
  "pos.offline.noQrOrders": "ออร์เดอร์ผ่าน QR ที่สั่งระหว่างออฟไลน์จะไม่ปรากฏ",
  "pos.offline.noOnlinePayment": "การชำระออนไลน์และยอดเงินสมาชิกใช้งานไม่ได้",
  "pos.offline.noReports": "รายงานและยอดสต๊อกจะไม่เป็นปัจจุบัน",
  "pos.offline.willSync": "ทุกอย่างที่รับไว้ตอนออฟไลน์จะถูกส่งเดี๋ยวนี้ อาจใช้เวลาสักครู่",
  "pos.sync.syncing": "กำลังซิงค์ข้อมูล…",
  "pos.sync.waiting": "รอส่ง {count} รายการ",
  "pos.sync.needsAttention": "ส่งไม่สำเร็จ {count} รายการ",
  "pos.failures.title": "ส่งไม่สำเร็จ",
  "pos.failures.intro":
    "เซิร์ฟเวอร์ปฏิเสธรายการเหล่านี้ และจะปฏิเสธต่อไป แต่ละรายการต้องมีคนตัดสินใจ โดยเฉพาะรายการชำระเงิน เพราะนั่นคือเงินของใครบางคน",
  "pos.failures.none": "ไม่มีรายการที่รอคุณอยู่",
  "pos.failures.money": "การชำระเงิน",
  "pos.failures.change": "เงินทอน",
  "pos.failures.queuedAt": "รับไว้เมื่อ {at}",
  "pos.failures.dismiss": "จัดการเรียบร้อยแล้ว",
  "pos.failures.close": "ปิด",

  "pos.bill.startTitle": "เปิดบิล",
  "pos.bill.startBody": "เลือกโต๊ะ หรือเปิดบิลสั่งกลับบ้าน ก่อนเพิ่มรายการ",
  "pos.bill.chooseTable": "เลือกโต๊ะ",
  "pos.bill.heldLabel": "{name} — พักบิลไว้",
  "pos.bill.newTakeAway": "สั่งกลับบ้าน",
  "pos.bill.itemsBlocked": "เปิดบิลก่อนจึงจะเพิ่มรายการได้",
  "pos.bill.open": "บิลที่เปิดอยู่",
  "pos.bill.opening": "กำลังเปิด\u2026",
  "pos.bill.notSent": "ยังไม่ได้ส่ง",
  "pos.bill.failed": "เปิดบิลนี้ไม่สำเร็จ",
  "pos.bill.clearTable": "เคลียร์โต๊ะ",
  "pos.bill.clearTableConfirm": "ปล่อย{table}หรือไม่ ยังไม่มีการสั่งอะไรบนโต๊ะนี้",
  "pos.bill.clearTableKeep": "เก็บไว้",
  "pos.bill.discardBill": "ทิ้งบิล",
  "pos.bill.discardBillConfirm": "ทิ้งบิลนี้หรือไม่ ยังไม่มีการสั่งอะไรบนบิลนี้",
  "pos.bill.discard": "ทิ้ง",
  "pos.bill.release": "ปล่อย",
  "pos.bill.occupied": "มีลูกค้าอยู่",
  "pos.bill.openThisBill": "เปิดบิลนี้",
  "pos.bill.resumeOffline": "บิลนี้ถูกเปิดจากเครื่องอื่น ต้องออนไลน์จึงจะเปิดที่นี่ได้",
  "pos.cart.table": "โต๊ะ",
  "pos.cart.takeAway": "สั่งกลับบ้าน",
  "pos.cart.covers": "{count} คน",
  "pos.cart.editCovers": "กดค้างเพื่อเปลี่ยนจำนวนคน",
  "pos.cart.empty": "ยังไม่มีรายการบนบิลนี้",
  "pos.cart.ordered": "สั่งแล้ว ({count})",
  "pos.cart.latestOrder": "รายการล่าสุด ({count})",
  "pos.cart.subtotal": "ยอดก่อนภาษี",
  "pos.cart.discount": "ส่วนลด",
  "pos.cart.addDiscount": "เพิ่ม",
  "pos.cart.service": "ค่าบริการ",
  "pos.cart.total": "ยอดรวม",
  "pos.cart.taxFree": "ยกเว้นภาษี",
  "pos.cart.taxFreeApply": "ใช้",
  "pos.cart.taxFreeRemove": "ยกเลิก",
  "pos.cart.hold": "พักบิล",
  "pos.cart.resume": "เรียกคืน",
  "pos.cart.sendToStation": "ส่งไปจุดผลิต",
  "pos.cart.settle": "ชำระเงิน",
  "pos.sync.busy": "กำลังซิงค์…",
  "pos.sync.failed": "ไม่สำเร็จ {count} รายการ",
  "pos.sync.queued": "รอคิว {count} รายการ",
  "pos.menu.all": "ทั้งหมด",
  "pos.menu.loading": "กำลังโหลดเมนู…",
  "pos.menu.soldOut": "ของหมด",
  "pos.menu.searchPlaceholder": "ค้นหาด้วยชื่อหรือรหัส",
  "pos.menu.result": "{count} รายการ",
  "pos.menu.results": "{count} รายการ",
  "pos.menu.inCart": "อยู่ในตะกร้า {count} รายการ",
  "pos.item.quantity": "จำนวน",
  "pos.item.note": "หมายเหตุถึงจุดผลิต",
  "pos.item.notePlaceholder": "ไม่ใส่น้ำแข็ง เผ็ดพิเศษ…",
  "pos.item.chooseOne": "เลือกหนึ่งอย่าง",
  "pos.item.chooseAny": "เลือกอย่างน้อยหนึ่งอย่าง",
  "pos.item.optional": "เลือกหรือไม่ก็ได้",
  "pos.item.cancel": "ยกเลิก",
  "pos.item.choose": "เลือก{group}",
  "pos.item.add": "เพิ่ม {total}",
  "pos.mode.online": "ออนไลน์",
  "pos.mode.offline": "ออฟไลน์",
  "pos.mode.goOffline": "สลับไปโหมดออฟไลน์หรือไม่",
  "pos.mode.comeOnline": "กลับมาออนไลน์หรือไม่",
  "pos.mode.stillAble": "เครื่องขายนี้ยังทำสิ่งเหล่านี้ได้",
  "pos.mode.butNot": "แต่ทำสิ่งเหล่านี้ไม่ได้",
  "pos.mode.allSent": "ส่งข้อมูลครบแล้วทั้งหมด",
  "pos.mode.stayOnline": "อยู่ออนไลน์ต่อ",
  "pos.mode.stayOffline": "อยู่ออฟไลน์ต่อ",
  "pos.mode.switchToOnline": "ไปออนไลน์",
  "pos.mode.switchToOffline": "ไปออฟไลน์",
  "pos.identify.heading": "สแกนเพื่อเข้าสู่ระบบ",
  "pos.identify.instruction": "หันกล้องโทรศัพท์ของคุณมาที่โค้ดนี้ แล้วยืนยันในแอปพนักงาน",
  "pos.identify.codeLabel": "หรือพิมพ์โค้ดนี้:",
  "pos.identify.denied": "การเข้าสู่ระบบนี้ถูกปฏิเสธ โปรดสแกนอีกครั้ง",
  "pos.identify.notConfigured":
    "เครื่องขายนี้ยังไม่ได้ตั้งค่าที่อยู่แอปพนักงาน จึงไม่มีใครเข้าสู่ระบบที่นี่ได้ โปรดสอบถามผู้ตั้งค่าเครื่องนี้",
  "pos.identify.unavailable":
    "ขณะนี้เครื่องขายนี้ไม่สามารถแสดงโค้ดเข้าสู่ระบบได้ โปรดตรวจสอบการเชื่อมต่อ",
  "pos.identify.retry": "ลองอีกครั้ง",
  "pos.identify.choice.checkIn": "ลงเวลาเข้างาน",
  "pos.identify.choice.take": "รับเครื่องขายนี้",
  "pos.identify.choice.checkInAndTake": "ลงเวลาเข้างานและรับเครื่องขายนี้",
  "pos.identify.choice.checkOut": "ลงเวลาออกงาน",
  "pos.identify.choice.cancel": "ยกเลิก",
  "pos.identify.choice.jobHeading": "ตำแหน่งงานไหน?",
  "pos.identify.choice.noJobs": "ร้านนี้ยังไม่ได้ตั้งค่าตำแหน่งงานใด ๆ",
  "pos.identify.choice.elsewhere": "ลงเวลาเข้างานที่ร้านอื่นอยู่แล้ว",
  "pos.identify.choice.checkedIn": "ลงเวลาเข้างานแล้ว",
  "pos.identify.choice.checkedOut": "ลงเวลาออกงานแล้ว",
  "pos.shift.title": "ลิ้นชักเก็บเงิน",
  "pos.shift.failed": "ทำรายการไม่สำเร็จ กรุณาลองใหม่",
  "pos.shift.handOver": "ส่งเวร",
  "pos.shift.noneOpen": "ยังไม่มีกะที่เปิดอยู่ นับเงินทอนตั้งต้นและเปิดกะเพื่อเริ่มรับเงิน",
  "pos.shift.openingFloat": "เงินทอนตั้งต้น",
  "pos.shift.open": "เปิดกะ",
  "pos.shift.openNow": "เปิด",
  "pos.shift.number": "กะที่ {number}",
  "pos.shift.openedBy": "เปิดกะโดย",
  "pos.shift.bills": "บิลที่ชำระแล้ว",
  "pos.shift.byMethod": "รับเงิน แยกตามวิธีชำระ",
  "pos.shift.nothingYet": "ยังไม่ได้รับเงิน",
  "pos.shift.expectedInDrawer": "ยอดที่ควรมีในลิ้นชัก",
  "pos.shift.railCounts": "แต่ละช่องทางแจ้งยอดเท่าไร",
  "pos.shift.railCountsHint": "ตรวจจากแอปธนาคารหรือเครื่องรูดบัตร หากไม่ได้ตรวจ ให้เว้นว่างไว้",
  "pos.shift.cardNotInDrawer": "ยอดบัตรและยอดออนไลน์ไม่ได้อยู่ในลิ้นชัก จึงไม่นับรวมตรงนี้",
  "pos.shift.counted": "นับได้",
  "pos.shift.balances": "ยอดตรงกัน",
  "pos.shift.short": "ขาด {amount}",
  "pos.shift.over": "เกิน {amount}",
  "pos.shift.close": "ปิดกะ",
  "pos.settle.title": "ชำระเงิน",
  "pos.settle.total": "ยอดรวม",
  "pos.settle.stillToPay": "ยังต้องชำระ",
  "pos.settle.change": "เงินทอน",
  "pos.settle.tip": "ทิป",
  "pos.settle.noTip": "ไม่ให้ทิป",
  "pos.settle.tipOther": "จำนวนอื่น",
  "pos.settle.due": "ต้องชำระ",
  "pos.station.title": "จุดส่งอาหาร",
  "pos.station.backToTill": "เครื่องขาย",
  "pos.station.all": "ทุกจุดผลิต",
  "pos.station.empty": "ไม่มีรายการที่ต้องทำ",
  "pos.station.waiting": "{minutes} นาที",
  "pos.station.course": "คอร์สที่ {number}",
  "pos.settle.amountTendered": "เงินที่รับมา",
  "pos.settle.back": "ย้อนกลับ",
  "pos.settle.done": "เสร็จสิ้น",
  "pos.billActions.title": "จัดการบิล",
  "pos.billActions.merge": "รวมบิล",
  "pos.billActions.mergeHint": "รวมบิลที่เปิดอยู่อีกใบเข้ากับบิลนี้ บิลนั้นจะถูกปิด",
  "pos.billActions.noOthers": "ไม่มีบิลอื่นที่เปิดอยู่",
  "pos.billActions.split": "แยกบิล",
  "pos.billActions.splitHint": "ย้ายรายการไปเป็นบิลของตัวเอง",
  "pos.billActions.splitDo": "ย้ายไปบิลใหม่",
  "pos.billActions.noFired": "ย้ายได้เฉพาะรายการที่ส่งจุดผลิตไปแล้วเท่านั้น",
  "pos.billActions.needsConnection": "การรวมและแยกบิลต้องใช้การเชื่อมต่อ",
  "pos.billActions.back": "ย้อนกลับ",
  "pos.line.title": "แก้ไขรายการ",
  "pos.line.void": "ยกเลิกรายการ",
  "pos.line.return": "ส่งคืน",
  "pos.line.back": "ย้อนกลับ",
  "pos.discount.title": "ส่วนลด",
  "pos.discount.amount": "จำนวนส่วนลด",
  "pos.discount.tooLarge": "ส่วนลดมากกว่ายอดบิลไม่ได้",
  "pos.discount.back": "ย้อนกลับ",
  "pos.discount.clear": "ล้าง",
  "pos.discount.apply": "ใช้ส่วนลด",
  "pos.table.title": "โต๊ะ",
  "pos.table.seats": "{count} ที่นั่ง",
  "pos.table.howMany": "{table} — กี่คน",
  "pos.table.status.free": "ว่าง",
  "pos.table.status.seated": "นั่งแล้ว",
  "pos.table.status.ordered": "สั่งแล้ว",
  "pos.table.status.billRequested": "เรียกเก็บเงิน",
  "pos.signIn.title": "ตั้งค่าเครื่องขายนี้",
  "pos.signIn.subtitle": "อนุมัติจากระบบหลังร้าน หลังจากนั้นพนักงานเข้าใช้ด้วยการสแกน",
  "pos.signIn.expiredTitle": "เข้าสู่ระบบเครื่องขายนี้อีกครั้ง",
  "pos.signIn.expiredSubtitle":
    "เซสชันของเครื่องนี้สิ้นสุดแล้ว ไม่มีข้อมูลสูญหาย รายการที่ยังรออยู่จะถูกส่งเมื่อเข้าระบบอีกครั้ง",
  "pos.signIn.codeLabel": "รหัสจับคู่",
  "pos.signIn.instruction": "ให้ผู้จัดการเปิด {uri} แล้วป้อนรหัสนี้ หรือสแกนรหัส",
  "pos.signIn.waiting": "กำลังรออนุมัติ…",
  "pos.signIn.retrying": "กำลังลองใหม่ — กำลังตรวจสอบการเชื่อมต่อ…",
  "pos.signIn.denied": "คำขอจับคู่ถูกปฏิเสธ",
  "pos.signIn.failed": "เกิดข้อผิดพลาด",
  "pos.signIn.newCode": "แสดงรหัสใหม่",

  "staff.signIn.title": "เข้าสู่ระบบ",
  "staff.signIn.subtitle": "ใช้พาสคีย์ของคุณ หรือชื่อผู้ใช้และรหัสผ่าน",
  "staff.signIn.passkey": "เข้าสู่ระบบด้วยพาสคีย์",
  "staff.signIn.or": "หรือ",
  "staff.signIn.username": "ชื่อผู้ใช้",
  "staff.signIn.password": "รหัสผ่าน",
  "staff.signIn.connect": "เข้าสู่ระบบ",
  "staff.signIn.rejected": "ข้อมูลนี้ใช้ไม่ได้",
  "staff.signIn.passkeyRejected": "พาสคีย์นั้นใช้ไม่ได้",
  "staff.signIn.unreachable": "ติดต่อเซิร์ฟเวอร์ไม่ได้",
  "staff.home.greeting": "เข้าสู่ระบบในนาม",
  "staff.home.signOut": "ออกจากระบบ",
  "staff.home.scan": "สแกนรหัสเครื่องขาย",
  "staff.home.scanRefused": "นั่นไม่ใช่รหัสเครื่องขายที่ถูกต้อง กรุณากรอกด้านล่างแทน",
  "staff.home.enterCode": "กรอกรหัสเครื่องขาย",
  "staff.home.enterCodePlaceholder": "เช่น ABCD-1234",
  "staff.home.enterCodeSubmit": "ดำเนินการต่อ",
  "staff.home.passkeyOffer": "เข้าสู่ระบบได้เร็วขึ้นในครั้งต่อไปด้วยพาสคีย์บนโทรศัพท์เครื่องนี้",
  "staff.home.passkeyAdd": "เพิ่มพาสคีย์",
  "staff.home.passkeyAdded": "เพิ่มพาสคีย์แล้ว ครั้งต่อไปให้เลือกเข้าสู่ระบบด้วยพาสคีย์",
  "staff.home.passkeyStale":
    "เพื่อความปลอดภัยของคุณ ให้ออกจากระบบแล้วเข้าสู่ระบบอีกครั้ง จากนั้นจึงเพิ่มพาสคีย์",
  "staff.home.passkeyFailed": "ไม่สามารถเพิ่มพาสคีย์บนอุปกรณ์นี้ได้",

  "staff.approve.continueAfterSignIn": "การเข้าสู่ระบบจะดำเนินการต่อไปยังคำขอของเครื่องขาย",
  "staff.approve.checking": "กำลังตรวจสอบรหัส…",
  "staff.approve.instruction": "ตรวจสอบว่าตรงกับรหัสที่แสดงบนเครื่องขาย แล้วยืนยัน",
  "staff.approve.take": "ใช่ฉันเอง",
  "staff.approve.notMe": "ไม่ใช่ฉัน",
  "staff.approve.approved": "ดำเนินการต่อที่เครื่องขาย",
  "staff.approve.backHome": "กลับหน้าแรก",
  "staff.approve.notForTill": "รหัสนี้ใช้จับคู่เครื่องขาย ไม่ใช่ระบุตัวผู้ที่ยืนอยู่หน้าเครื่อง",
  "staff.approve.taken": "มีคนอื่นกำลังอนุมัติรหัสนี้อยู่แล้ว",
  "staff.approve.used": "รหัสนี้ถูกใช้ไปแล้ว",
  "staff.approve.gone": "รหัสนี้ใช้ไม่ได้อีกต่อไป",
  "staff.approve.refused": "บัญชีนี้ไม่สามารถรับเครื่องขายได้",
  "staff.approve.retry": "ลองอีกครั้ง",

  "staff.approve.someoneElse": "คนอื่น",
  "staff.approve.pickEmployee": "มอบหมายให้ใครสักคนใช้เครื่องขายนี้",
  "staff.approve.search": "ค้นหา",
  "staff.approve.confirmFor": "มอบเครื่องขายนี้ให้ {name} หรือไม่",
  "staff.approve.assigned": "{name} จะเข้าสู่ระบบที่เครื่องขายในอีกไม่กี่วินาที",
  "staff.approve.assignRefused": "ไม่สามารถมอบหมายให้บุคคลนั้นใช้เครื่องขายได้",

  "diner.title": "สั่งอาหาร",
  "diner.scanPrompt": "สแกนรหัสบนโต๊ะเพื่อดูเมนู",
  "diner.takeaway": "สั่งกลับบ้าน",
  "diner.closed": "ขณะนี้ปิดรับออร์เดอร์",
  "diner.all": "ทั้งหมด",
  "diner.soldOut": "ของหมด",
  "voice.newOrder": "มีออร์เดอร์ใหม่",
  "diner.sold": "ขายแล้ว {count}",
  "diner.sending": "กำลังส่ง…",
  "diner.order": "สั่ง {count} รายการ · {total}",
  "diner.sendFailed": "ส่งไม่สำเร็จ",
  "diner.locating": "กำลังตรวจสอบตำแหน่งของคุณ…",
  "diner.location.denied":
    "ร้านนี้ต้องใช้ตำแหน่งของคุณเพื่อรับออร์เดอร์ โปรดอนุญาตการเข้าถึงตำแหน่งแล้วลองอีกครั้ง",
  "diner.location.unavailable": "ไม่พบตำแหน่งของคุณ โปรดตรวจสอบว่าเปิดตำแหน่งอยู่แล้วลองอีกครั้ง",
  "diner.chooseOne": "· เลือกหนึ่งอย่าง",
  "diner.chooseRange": "· เลือก {min}–{max} อย่าง",
  "diner.chooseUpTo": "· ไม่เกิน {max} อย่าง",
  "diner.extras": "ของเพิ่ม",
  "diner.noteLabel": "มีอะไรอยากบอกเราไหม",
  "diner.notePlaceholder": "ไม่ใส่พริก",
  "diner.chooseGroup": "เลือก{group}",
  "diner.add": "เพิ่ม · {total}",
  "diner.yourNumber": "หมายเลขของคุณ",
  "diner.status.pending": "ส่งเข้าครัวแล้ว",
  "diner.status.accepted": "รับออร์เดอร์แล้ว",
  "diner.status.preparing": "กำลังทำ",
  "diner.status.served": "เสิร์ฟแล้ว",
  "diner.discount": "ส่วนลด",
  "diner.service": "ค่าบริการ",
  "diner.total": "ยอดรวม",
  "diner.paid": "ชำระเงินแล้ว ขอบคุณ",
  "diner.payFirst": "กรุณาชำระเงินเพื่อยืนยันออร์เดอร์",
  "diner.payAtCounter": "ชำระเงินที่เคาน์เตอร์เมื่อพร้อม",
  "diner.couponCode": "รหัสคูปอง",
  "diner.couponFailed": "รหัสนี้ใช้ไม่ได้",
  "diner.member.signIn": "เข้าสู่ระบบ",
  "diner.member.signInTitle": "สมาชิกของคุณ",
  "diner.member.signInExplainer":
    "เข้าสู่ระบบด้วยเบอร์โทรศัพท์เพื่อดูคะแนน ยอดเงิน และคูปองของคุณ และสะสมคะแนนจากบิลนี้",
  "diner.member.phone": "เบอร์โทรศัพท์",
  "diner.member.sendCode": "ส่งรหัสให้ฉัน",
  "diner.member.codeSent": "ส่งรหัสไปที่ {phone} แล้ว",
  "diner.member.devCode":
    "เซิร์ฟเวอร์นี้ยังไม่ได้ตั้งค่าผู้ให้บริการ SMS จึงแสดงรหัสไว้ที่นี่: {code}",
  "diner.member.code": "รหัส",
  "diner.member.nameOptional": "ชื่อของคุณ (กรอกเฉพาะกรณีที่เพิ่งมาครั้งแรก)",
  "diner.member.differentNumber": "ใช้เบอร์อื่น",
  "diner.member.failed": "ทำรายการไม่สำเร็จ กรุณาลองใหม่",
  "diner.member.title": "สมาชิกของคุณ",
  "diner.member.number": "สมาชิกเลขที่ {number}",
  "diner.member.points": "คะแนน",
  "diner.member.points.short": "{points} คะแนน",
  "diner.member.pointsWorth": "มีมูลค่า {amount} ที่ร้านนี้",
  "diner.member.noPointsScheme": "ร้านนี้ไม่มีระบบสะสมคะแนน",
  "diner.member.pointsHistory": "คะแนนล่าสุด",
  "diner.member.balance": "ยอดคงเหลือ",
  "diner.member.held": "กันยอด {amount} ไว้กับบิลที่เปิดอยู่",
  "diner.member.pinNeeded": "ระบบจะขอรหัส PIN ของคุณเมื่อชำระด้วยวิธีนี้",
  "diner.member.topUp": "เติมเงิน",
  "diner.member.topUpAtCounter": "สอบถามที่เคาน์เตอร์เพื่อเติมเงิน ยังทำจากที่นี่ไม่ได้",
  "diner.member.getsYou": "ได้รับ {amount}",
  "diner.member.coupons": "คูปอง",
  "diner.member.noCoupons": "คุณยังไม่มีคูปอง",
  "diner.member.minSpend": "ซื้อครบ {amount}",
  "diner.member.coupon.issued": "พร้อมใช้",
  "diner.member.coupon.used": "ใช้แล้ว",
  "diner.member.coupon.expired": "หมดอายุ",
  "diner.member.coupon.void": "ถูกยกเลิก",
  "diner.member.earnOnThisBill": "เข้าสู่ระบบเพื่อสะสมคะแนนจากบิลนี้",
  "diner.member.addToThisBill": "เพิ่ม {name} เข้ากับบิลนี้เพื่อสะสมคะแนน",
  "diner.member.claim": "เพิ่มฉันเข้ากับบิลนี้",
  "diner.member.onThisBill": "{name} อยู่บนบิลนี้แล้ว",
  "diner.apply": "ใช้",
  "diner.orderMore": "สั่งเพิ่ม",
  "diner.cancelled": "ออร์เดอร์นี้ถูกยกเลิก",
  "reports.title": "รายงาน",
  "reports.description": "ตัวเลขเบื้องหลังการให้บริการ ตามวันขายของร้านคุณเอง",
  "reports.pick": "รายงาน",
  "reports.from": "ตั้งแต่",
  "reports.to": "ถึง",
  "reports.consolidate": "รวมทั้งช่วงเป็นแถวเดียว",
  "reports.groupBy": "จัดกลุ่มตาม",
  "reports.columns": "คอลัมน์",
  "reports.export": "ส่งออก",
  "reports.exported": "เข้าคิวแล้ว จะปรากฏในหน้าการส่งออกข้อมูล",
  "reports.total": "ยอดรวม",
  "reports.empty": "ไม่มียอดในช่วงเวลานี้",
  "reports.master": "รายงานรวม",
  "reports.itemCategory": "รายการและหมวดหมู่",
  "reports.paymentMethods": "วิธีชำระเงิน",
  "reports.hourlySales": "ยอดขายรายชั่วโมง",
  "reports.discounts": "ส่วนลด",
  "reports.voids": "รายการที่ยกเลิก",
  "reports.topSelling": "ขายดีที่สุด",
  "reports.daily": "รายวัน",
  "reports.tables": "โต๊ะ",
  "reports.shifts": "กะ",
  "reports.credit": "บิลค้างชำระ",
  "reports.stock": "ความเคลื่อนไหวสต๊อก",
  "reports.column.initialStock": "สต๊อกยกมา",
  "reports.column.inbound": "รับเข้า",
  "reports.column.outbound": "เบิกออก",
  "reports.column.endingStock": "สต๊อกคงเหลือ",
  "reports.column.group": "แยกตาม",
  "reports.column.store": "สาขา",
  "reports.column.orders": "จำนวนออร์เดอร์",
  "reports.column.covers": "จำนวนผู้ทาน",
  "reports.column.grossSales": "ยอดขายรวม",
  "reports.column.discount": "ส่วนลด",
  "reports.column.campaignDiscount": "ส่วนลดแคมเปญ",
  "reports.column.netExTax": "ยอดสุทธิก่อนภาษี",
  "reports.column.netSales": "ยอดขายสุทธิ",
  "reports.column.tax": "ภาษี",
  "reports.column.serviceCharge": "ค่าบริการ",
  "reports.column.netIncTax": "ยอดสุทธิรวมภาษี",
  "reports.column.payments": "รับเงิน",
  "reports.column.refunds": "คืนเงิน",
  "reports.column.averageOrder": "ยอดบิลเฉลี่ย",
  "reports.column.averageCover": "เฉลี่ยต่อคน",
  "reports.column.quantity": "จำนวน",
  "reports.column.method": "วิธีชำระ",
  "reports.column.tenders": "จำนวนรายการชำระ",
  "reports.column.taken": "รับเงิน",
  "reports.column.net": "สุทธิ",
  "reports.column.hour": "ชั่วโมง",
  "reports.column.orderNo": "เลขที่ออร์เดอร์",
  "reports.column.date": "วันที่",
  "reports.column.who": "ผู้ดำเนินการ",
  "reports.column.what": "รายการ",
  "reports.column.detail": "รายละเอียด",
  "reports.column.reason": "เหตุผล",
  "reports.column.item": "รายการ",
  "reports.column.rankByQuantity": "อันดับตามจำนวน",
  "reports.column.rankByRevenue": "อันดับตามยอดขาย",
  "reports.column.table": "โต๊ะ",
  "reports.column.turnTime": "นาทีต่อรอบโต๊ะ",
  "reports.column.shift": "กะ",
  "reports.column.openedBy": "เปิดกะโดย",
  "reports.column.closedBy": "ปิดกะโดย",
  "reports.column.float": "เงินทอนตั้งต้น",
  "reports.column.expectedCash": "ยอดที่ควรมีในลิ้นชัก",
  "reports.column.countedCash": "นับได้",
  "reports.column.difference": "ผลต่าง",
  "reports.column.customer": "ลูกค้า",
  "reports.column.amount": "จำนวนเงิน",
  "reports.column.settled": "ชำระแล้ว",
  "reports.column.outstanding": "ค้างชำระ",
  "reports.column.ageDays": "ค้างมาแล้ว (วัน)",
  "reports.column.status": "สถานะ",
  "reports.hourlySalesDetails": "ยอดขายรายชั่วโมงแยกตามรายการ",
  "reports.newItems": "เมนูใหม่",
  "reports.coupons": "คูปอง",
  "reports.discountPromos": "แคมเปญโปรโมชัน",
  "reports.sales": "ยอดขายแยกตามพนักงาน",
  "reports.items": "รายการและของเสีย",
  "reports.storeCosts": "ค่าใช้จ่ายร้าน",
  "reports.itemCosts": "ต้นทุนรายการ",
  "reports.profit": "กำไร",
  "reports.tipReporting": "รายงานทิป (8027)",
  "reports.tipsByOccupation": "ทิปแยกตามตำแหน่งงาน",
  "reports.labourHours": "ชั่วโมงทำงาน",
  "reports.column.grossReceipts": "รายรับรวม",
  "reports.column.chargedTips": "ทิปที่จ่ายผ่านบัตร",
  "reports.column.chargeReceipts": "บิลบัตรที่มีทิป",
  "reports.column.cashTips": "ทิปเงินสด",
  "reports.column.directlyTipped": "จ่ายให้ผู้รับทิปโดยตรง",
  "reports.column.indirectlyTipped": "จ่ายให้ผู้รับทิปทางอ้อม",
  "reports.column.eightPercent": "8% ของรายรับรวม",
  "reports.column.job": "ตำแหน่งงาน",
  "reports.column.occupationCode": "รหัสตำแหน่งงาน",
  "reports.column.tipStatus": "สถานะทิป",
  "reports.column.businessDate": "วันขาย",
  "reports.column.openEntries": "ยังไม่ลงเวลาออก",
  "reports.column.minutes": "นาที",
  "reports.unattributed": "ไม่ระบุพนักงาน",
  "reports.column.averageAmount": "จำนวนเงินเฉลี่ย",
  "reports.column.addedOn": "เพิ่มเมื่อ",
  "reports.column.labels": "ป้ายกำกับ",
  "reports.column.quantityShare": "สัดส่วนจำนวน",
  "reports.column.amountShare": "สัดส่วนยอดขาย",
  "reports.column.coupon": "คูปอง",
  "reports.column.issued": "ออกแล้ว",
  "reports.column.redeemed": "ใช้แล้ว",
  "reports.column.expired": "หมดอายุ",
  "reports.column.redemptionRate": "อัตราการใช้คูปอง",
  "reports.column.orderAmount": "ยอดบิล",
  "reports.column.discountShare": "สัดส่วนส่วนลดต่อบิล",
  "reports.column.promo": "แคมเปญ",
  "reports.column.returnedQuantity": "ส่งคืน",
  "reports.column.voidedQuantity": "ยกเลิก",
  "reports.column.wastageRate": "อัตราของเสีย",
  "reports.column.received": "รับเข้า",
  "reports.column.supplyCost": "ต้นทุนวัสดุ",
  "reports.column.marketingCost": "การตลาด",
  "reports.column.laborCost": "ค่าแรง",
  "reports.column.administrativeCost": "ค่าใช้จ่ายทั่วไป",
  "reports.column.adHocCost": "รายการครั้งเดียว",
  "reports.column.totalCosts": "ต้นทุนรวม",
  "reports.column.profit": "กำไร",
  "reports.column.grossMargin": "อัตรากำไร",
  "reports.column.openingCash": "เงินสดยกมา",
  "reports.column.salesQty": "จำนวนที่ขายได้",
  "reports.column.averageCost": "ต้นทุนเฉลี่ย",
  "reports.column.originalPaid": "ก่อนส่วนลด",
  "reports.column.actualPaid": "หลังส่วนลด",
  "reports.column.marginBeforeDiscount": "อัตรากำไรก่อนส่วนลด",
  "reports.column.marginAfterDiscount": "อัตรากำไรหลังส่วนลด",
  "reports.column.creditTaken": "ลงบิลค้างชำระ",
  "reports.column.expense": "จ่ายออก",
  "reports.column.profitBalance": "ยอดกำไรคงเหลือ",
  "costs.title": "ค่าใช้จ่ายร้าน",
  "costs.description": "ธุรกิจใช้จ่ายอะไรบ้าง เทียบกับรายรับ",
  "costs.add": "เพิ่มค่าใช้จ่าย",
  "costs.edit": "แก้ไขค่าใช้จ่าย",
  "costs.date": "วันขาย",
  "costs.type": "ค่าอะไร",
  "costs.amount": "จำนวนเงิน",
  "costs.note": "หมายเหตุ",
  "costs.category": "กลุ่ม",
  "costs.enteredBy": "ผู้บันทึก",
  "costs.empty": "ยังไม่มีการบันทึกในช่วงเวลานี้",
  "costs.deleteTitle": "ลบรายการค่าใช้จ่ายนี้หรือไม่",
  "costs.deleteBody": "รายงานค่าใช้จ่ายร้านจะไม่นับรายการนี้อีก ยอดขายของวันนั้นไม่เปลี่ยนแปลง",
  "costs.record": "ประวัติ",
  "costs.category.marketing": "การตลาด",
  "costs.category.labor": "ค่าแรง",
  "costs.category.administrative": "ค่าใช้จ่ายทั่วไป",
  "costs.category.ad_hoc": "รายการครั้งเดียว",
  "costs.type.platformFee": "ค่าธรรมเนียมแพลตฟอร์ม",
  "costs.type.advertising": "ค่าโฆษณา",
  "costs.type.marketingDiscounts": "ส่วนลดส่งเสริมการขาย",
  "costs.type.deliveryFee": "ค่าขนส่งและจัดส่ง",
  "costs.type.serviceFee": "ค่าธรรมเนียมบริการ",
  "costs.type.salary": "เงินเดือน",
  "costs.type.salesCommission": "ค่าคอมมิชชันการขาย",
  "costs.type.overtime": "ค่าล่วงเวลา",
  "costs.type.insurance": "ค่าประกัน",
  "costs.type.benefitsTraining": "สวัสดิการและการฝึกอบรม",
  "costs.type.accommodation": "ค่าที่พัก",
  "costs.type.transportation": "ค่าเดินทาง",
  "costs.type.travel": "ค่าเบี้ยเลี้ยงเดินทาง",
  "costs.type.rent": "ค่าเช่า",
  "costs.type.propertyFee": "ค่าส่วนกลางอาคาร",
  "costs.type.water": "ค่าน้ำ",
  "costs.type.electricity": "ค่าไฟฟ้า",
  "costs.type.gas": "ค่าแก๊ส",
  "costs.type.renovation": "ค่าตกแต่ง ตัดจ่าย",
  "costs.type.equipmentPurchase": "ค่าซื้ออุปกรณ์",
  "costs.type.equipmentRepair": "ค่าซ่อมอุปกรณ์",
  "costs.type.depreciation": "ค่าเสื่อมราคาอุปกรณ์",
  "costs.type.adHoc": "ค่าใช้จ่ายครั้งเดียว",
  "exports.title": "การส่งออกข้อมูล",
  "exports.description": "ไฟล์ที่คุณขอไว้ และสถานะว่าพร้อมหรือยัง",
  "exports.report": "รายงาน",
  "exports.period": "ช่วงเวลา",
  "exports.rows": "จำนวนแถว",
  "exports.requestedBy": "ผู้ขอ",
  "exports.requested": "ขอเมื่อ",
  "exports.download": "ดาวน์โหลด",
  "exports.expiresOn": "ใช้ได้ถึง {date}",
  "exports.expired": "ไม่พร้อมใช้แล้ว กรุณาขอใหม่",
  "exports.record": "การส่งออกข้อมูล",
  "exports.completed": "เสร็จเมื่อ",
  "exports.availability": "ความพร้อมใช้",
  "exports.failure": "สาเหตุที่ไม่สำเร็จ",
  "exportStatus.queued": "รอคิว",
  "exportStatus.running": "กำลังประมวลผล",
  "exportStatus.ready": "พร้อมแล้ว",
  "exportStatus.failed": "ไม่สำเร็จ",

  "dashboard.title": "วันนี้",
  "dashboard.description": "วันนี้เป็นอย่างไร เทียบกับวันเดียวกันของสัปดาห์ก่อน",
  "dashboard.updated": "อัปเดตเมื่อ",
  "dashboard.refresh": "อัปเดตเดี๋ยวนี้",
  "dashboard.vsLastWeek": "เทียบกับวันเดียวกันสัปดาห์ก่อน",
  "dashboard.byMethod": "แยกตามวิธีชำระเงิน",
  "dashboard.ranking": "ขายดีที่สุด",
  "dashboard.daily": "แยกตามวัน",
  "dashboard.money.totalSales": "ยอดขายรวม",
  "dashboard.money.received": "รับเงินแล้ว",
  "dashboard.money.credit": "ค้างชำระ",
  "dashboard.money.unpaid": "ยังค้างอยู่",
  "dashboard.money.discount": "ส่วนลด",
  "dashboard.money.refund": "คืนเงิน",
  "dashboard.money.cancelled": "ยกเลิก",
  "dashboard.counts.orders": "ออร์เดอร์",
  "dashboard.counts.receivedOrders": "ชำระแล้ว",
  "dashboard.counts.creditOrders": "ค้างชำระ",
  "dashboard.counts.unpaidOrders": "ยังไม่ชำระ",
  "dashboard.counts.discountOrders": "มีส่วนลด",
  "dashboard.counts.refundOrders": "คืนเงิน",
  "dashboard.counts.cancelledOrders": "ยกเลิก",

  "auth.signIn": "เข้าสู่ระบบ",
  "auth.signOut": "ออกจากระบบ",
  "auth.email": "อีเมล",
  "auth.password": "รหัสผ่าน",
  "auth.signingIn": "กำลังเข้าสู่ระบบ…",
  "auth.invalidCredentials": "อีเมลและรหัสผ่านนี้ไม่ตรงกับบัญชีใด",
  "auth.title": "เข้าสู่ระบบ TOVI",
  "auth.subtitle": "ระบบหลังร้าน",
  "auth.showPassword": "แสดงรหัสผ่าน",
  "auth.operatorTitle": "เข้าสู่ระบบ TOVI Operator",
  "auth.operatorSubtitle": "สำหรับพนักงานแพลตฟอร์มเท่านั้น",
  "auth.operatorAccountTitle": "บัญชีนี้เป็นของคอนโซลผู้ดูแลระบบ",
  "auth.operatorAccountBody": "เข้าสู่ระบบด้วยบัญชีร้านค้าเพื่อใช้ระบบหลังร้าน",
  "auth.merchantAccountTitle": "บัญชีนี้เป็นของระบบหลังร้าน",
  "auth.merchantAccountBody": "เข้าสู่ระบบด้วยบัญชีผู้ดูแลระบบเพื่อใช้คอนโซลนี้",
  "auth.accountUnusableTitle": "บัญชีนี้ไม่สามารถใช้คอนโซลนี้ได้",
  "auth.accountUnusableBody": "อาจถูกปิดใช้งานหรือยังตั้งค่าไม่เสร็จ ให้ผู้ที่ดูแลบัญชีตรวจสอบ",

  "shell.store": "สาขา",
  "shell.role": "บทบาท",
  "shell.language": "ภาษา",
  "shell.loading": "กำลังโหลด…",
  "shell.openNav": "เปิดเมนูนำทาง",
  "shell.closeNav": "ปิดเมนูนำทาง",
  "shell.notifications": "การแจ้งเตือน",
  "shell.account": "เมนูบัญชี",
  "shell.venue": "สาขาปัจจุบัน",
  "shell.settings": "ตั้งค่า",
  "shell.emptyTitle": "ยังไม่มีข้อมูล",
  "shell.emptyBody": "นี่คือส่วนที่ต้องเข้าสู่ระบบ เมนู ออร์เดอร์ และรายงานจะมาในเฟสถัดไป",

  "error.generic": "เกิดข้อผิดพลาดบางอย่าง",
  "error.notFound": "ไม่พบข้อมูล",
  "error.forbidden": "คุณไม่มีสิทธิ์ทำสิ่งนี้",
  "error.notEntitled": "สิ่งนี้ไม่รวมอยู่ในแพ็กเกจของคุณ",
  "error.quotaExceeded": "คุณใช้ครบตามจำนวนที่กำหนดแล้ว",

  /**
   * A refusal a merchant or a cashier actually meets, keyed by the `reason` the
   * API sends beside the code (`packages/shared/src/http/error-reasons.ts`).
   * The four keys above are the generic sentences; these are the specific ones,
   * and `resolveErrorMessage` prefers them over the server's English.
   */
  "error.inventoryForbidden": "คุณไม่มีสิทธิ์เข้าถึงคลังส่วนนั้น",
  "error.capabilityNotDelegated": "สำนักงานใหญ่ยังไม่ได้เปิดสิ่งนี้ให้ร้านนี้",
  "error.rateLimited": "พยายามหลายครั้งเกินไป — โปรดลองใหม่อีกสักครู่",
  "error.signInRequired": "โปรดเข้าสู่ระบบเพื่อดำเนินการต่อ",
  "error.accountDisabled": "บัญชีนี้ถูกปิดใช้งาน",
  "error.accountNoStore": "บัญชีนี้ยังไม่ได้ผูกกับร้านใด",
  "error.storeNotAllowed": "คุณไม่สามารถทำงานที่ร้านนั้นได้",
  "error.pinRequired": "โปรดเข้าสู่ระบบที่เครื่องขายนี้เพื่อดำเนินการต่อ",
  "error.pinSessionExpired": "การเข้าสู่ระบบของคุณที่เครื่องขายนี้หมดอายุแล้ว โปรดสแกนอีกครั้ง",
  "error.pinMismatch": "PIN ไม่ตรงกัน",
  "error.terminalRequired": "มีเพียงเครื่องแคชเชียร์เท่านั้นที่ระบุตัวพนักงานได้",
  "error.grantNotRecognised": "การเข้าสู่ระบบนี้หมดอายุแล้ว — โปรดสแกนโค้ดอีกครั้ง",
  "error.deviceCodeNotPending": "ไม่สามารถอนุมัติรหัสนี้แทนผู้อื่นได้",
  "error.alreadyClockedIn": "ลงเวลาเข้างานไว้แล้ว",
  "error.notClockedIn": "ยังไม่ได้ลงเวลาเข้างาน",
  "error.clockedInElsewhere": "ลงเวลาเข้างานที่ร้านอื่น ไม่ใช่ที่นี่",
  "error.unitInUse": "มีวัสดุเก็บสต๊อกด้วยหน่วยนับนี้อยู่",
  "error.unitSeeded": "หน่วยนับมาตรฐานลบไม่ได้ ทำได้เพียงซ่อนด้วยการเรียงลำดับ",
  "error.unitSelfConversion": "หน่วยนับแปลงเป็นตัวเองในอัตราหนึ่งต่อหนึ่ง",
  "error.unitDimensionMismatch": "หน่วยนับสองหน่วยนี้วัดคนละสิ่ง จึงแปลงกันไม่ได้",
  "error.supplyCategoryHasChildren": "โปรดล้างหมวดหมู่ย่อยก่อน",
  "error.supplyCategoryInUse": "มีวัสดุอยู่ในหมวดหมู่นี้",
  "error.supplyCategorySelfParent": "หมวดหมู่บรรจุตัวเองไม่ได้",
  "error.supplyCategoryTooDeep": "หมวดหมู่มีได้สองระดับเท่านั้น",
  "error.brandInUse": "มีวัสดุใช้แบรนด์นี้อยู่",
  "error.supplyHasStock": "ยังมีสต๊อกของวัสดุนี้ โปรดตัดออกก่อน",
  "error.supplyNumberTaken": "หมายเลขนี้ถูกใช้แล้ว",
  "error.barcodeTaken": "บาร์โค้ดนี้เป็นของวัสดุอื่นแล้ว",
  "error.menuCategoryInUse": "โปรดย้ายหรือลบรายการในหมวดหมู่นี้ก่อน",
  "error.itemNumberTaken": "มีรายการอื่นใช้รหัสนี้แล้ว",
  "error.tableAreaInUse": "โปรดย้ายหรือลบโต๊ะในโซนนี้ก่อน",
  "error.stationHasPrinters": "โปรดย้ายเครื่องพิมพ์ของจุดผลิตนี้ไปที่อื่นก่อน",
  "error.stationHasItems": "โปรดเปลี่ยนปลายทางรายการของจุดผลิตนี้ก่อน",
  "error.printerHasItems": "บางรายการพิมพ์ที่เครื่องนี้เท่านั้น โปรดเปลี่ยนปลายทางก่อน",
  "error.printerNeedsAddress": "โปรดกำหนดที่อยู่ให้เครื่องพิมพ์นี้ก่อนจับคู่",
  "error.printerNeedsStation": "เครื่องพิมพ์ประจำจุดผลิตต้องมีจุดผลิตให้ส่งรายการไป",
  "error.paymentMethodBuiltIn": "วิธีชำระเงินนี้เป็นค่ามาตรฐานและลบไม่ได้ ให้ปิดใช้งานแทน",
  "error.tipPoolPaidOut": "กองทิปนี้จ่ายออกไปแล้ว ให้ปิดใช้งานแทนการลบ",
  "error.tableAlreadyHasBill": "โต๊ะนี้มีบิลเปิดอยู่แล้ว",
  "error.billAlreadySettled": "บิลนี้ชำระเงินแล้ว",
  "error.billNotCheckedOut": "บิลนี้ยังไม่ได้เช็คบิล",
  "error.orderCancelled": "ออร์เดอร์นี้ถูกยกเลิกแล้ว",
  "error.settledBillNeedsReverse": "โปรดย้อนการชำระก่อนแก้ไขบิลที่ชำระแล้ว",
  "error.moneyTakenUseRefund": "รับเงินไปแล้ว ให้คืนเงินหรือย้อนการชำระแทน",
  "error.nothingTakenYet": "ยังไม่ได้รับเงิน",
  "error.nothingLeftToRefund": "ไม่มียอดเหลือให้คืนเงิน",
  "error.refundExceedsTaken": "มากกว่ายอดที่รับมา",
  "error.paymentExceedsOutstanding": "มากกว่ายอดค้างชำระของบิล",
  "error.paymentMethodUnavailable": "ใช้วิธีชำระเงินนี้ที่นี่ไม่ได้",
  "error.paymentProviderNotInstalled": "วิธีชำระเงินนี้อ้างถึงผู้ให้บริการที่ยังไม่ได้ติดตั้ง",
  "error.paymentRailPending": "การชำระเงินนี้ยังไม่เสร็จ โปรดลองใหม่อีกสักครู่",
  "error.paymentAsyncUnsupported":
    "วิธีชำระเงินนี้ตัดยอดหลังผู้จ่ายทำรายการ ซึ่งเครื่องขายนี้ยังรอไม่ได้",
  "error.discountExceedsBill": "ส่วนลดมากกว่ายอดบิลไม่ได้",
  "error.returnExceedsOrdered": "คืนมากกว่าที่สั่ง",
  "error.takingsCounted": "ยอดขายของวันนั้นนับแล้ว แก้ทิปได้ก่อนปิดวันเท่านั้น",
  "error.tipNotSupportedOnTender": "การชำระเงินแบบนี้เพิ่มทิปไม่ได้",
  "error.mergeIntoSelf": "รวมออร์เดอร์เข้ากับตัวเองไม่ได้",
  "error.mergeSourcePaid": "บิลนั้นรับเงินไปแล้ว",
  "error.splitNoSeats": "ไม่มีรายการใดในบิลนี้ระบุที่นั่ง",
  "error.splitSingleSeat": "ทุกรายการอยู่ที่นั่งเดียวกัน จึงไม่มีอะไรให้แยก",
  "error.splitLeavesEmptyBill": "แยกทุกรายการออกจะทำให้บิลว่าง",
  "error.courseNothingWaiting": "ไม่มีรายการในคอร์สนั้นรอทำ",
  "error.giftCardCodeRequired": "บัตรของขวัญใบไหน ต้องมีรหัสจึงจะใช้ได้",
  "error.giftCardVoided": "บัตรนี้ถูกยกเลิกแล้ว",
  "error.giftCardExpired": "บัตรนี้หมดอายุแล้ว",
  "error.giftCardCurrencyMismatch": "บัตรนี้เป็นสกุลเงินอื่น",
  "error.giftCardInsufficient": "บัตรนี้มียอดไม่พอ",
  "error.memberRequired": "โปรดระบุสมาชิกก่อนใช้ยอดคงเหลือ",
  "error.memberBlocked": "สมาชิกรายนี้ถูกระงับ",
  "error.memberBalanceInsufficient": "มากกว่ายอดคงเหลือของสมาชิก",
  "error.memberPointsInsufficient": "มากกว่าคะแนนที่สมาชิกมี",
  "error.tabClosed": "บิลค้างชำระนี้ปิดแล้ว",
  "error.tabNothingLeft": "ไม่มียอดเหลือในบิลค้างชำระนี้",
  "error.tabRepayExceeds": "มากกว่ายอดที่บิลค้างชำระค้างอยู่",
  "error.tabRepaidWithTab": "ชำระบิลค้างชำระด้วยบิลค้างชำระอีกใบไม่ได้",
  "error.couponSwitchedOff": "คูปองนี้ถูกปิดใช้งาน",
  "error.couponUsed": "คูปองนี้ถูกใช้ไปแล้ว",
  "error.couponExpired": "คูปองนี้หมดอายุแล้ว",
  "error.couponNotYetValid": "คูปองนี้ยังใช้ไม่ได้",
  "error.couponOtherMember": "คูปองนี้เป็นของสมาชิกที่ไม่ได้อยู่ในบิลนี้",
  "error.couponBillEmpty": "ยังไม่มีรายการในบิลนี้",
  "error.couponBelowMinimum": "บิลนี้ยังไม่ถึงยอดขั้นต่ำของคูปอง",
  "error.orderLocationRequired": "ร้านนี้ต้องใช้ตำแหน่งของคุณเพื่อรับออร์เดอร์",
  "error.orderTooFar": "คุณอยู่ไกลจากร้านเกินกว่าจะสั่งได้",
  "error.shiftAlreadyOpen": "มีกะเปิดอยู่แล้ว โปรดปิดก่อนเปิดกะใหม่",
  "error.shiftAlreadyClosed": "กะนี้ปิดแล้ว",
  "error.noShiftOpen": "ไม่มีกะที่เปิดอยู่",
  "error.shiftTenderNothingTaken": "กะนี้ไม่มียอดรับเงินจากช่องทางใดช่องทางหนึ่งในนั้น",
  "error.lastRoleEditor": "นี่คือบทบาทสุดท้ายที่แก้ไขบทบาทได้",
  "error.photoNotJpeg": "นั่นไม่ใช่ภาพ JPEG",
  "error.photoTooLarge": "รูปภาพนั้นมีขนาดใหญ่เกินไป",
  "error.passwordForSelf": "คุณไม่สามารถตั้งรหัสผ่านของตัวเองที่นี่ได้",
  "error.employeeOutranksYou":
    "บุคคลนี้สามารถทำสิ่งที่บทบาทของคุณทำไม่ได้ คุณจึงไม่สามารถตั้งรหัสผ่านให้เขาได้",
  "error.roleOutranksYou": "คุณไม่สามารถมอบบทบาทที่ทำได้มากกว่าบทบาทของคุณได้",

  "operator.shell.console": "คอนโซลผู้ดูแลระบบ",
  "operator.shell.noRole": "ไม่มีบทบาท",

  "operator.nav.overview": "ภาพรวม",
  "operator.nav.merchants": "ร้านค้า",
  "operator.nav.catalog": "แคตตาล็อก",
  "operator.nav.finance": "การเงิน",
  "operator.nav.platform": "แพลตฟอร์ม",

  "operator.field.merchant": "ร้านค้า",
  "operator.field.merchants": "ร้านค้า",
  "operator.field.plan": "แพ็กเกจ",
  "operator.field.status": "สถานะ",
  "operator.field.country": "ประเทศ",
  "operator.field.partner": "พาร์ตเนอร์",
  "operator.field.code": "รหัส",
  "operator.field.scope": "ขอบเขต",
  "operator.field.price": "ราคา",
  "operator.field.tier": "ระดับ",
  "operator.field.expires": "หมดอายุ",
  "operator.field.stores": "สาขา",
  "operator.field.transactions": "รายการชำระ",
  "operator.field.permanent": "ถาวร",
  "operator.field.perMonth": "{price}/เดือน",
  "operator.field.monthly": "รายเดือน",
  "operator.field.yearly": "รายปี",

  "operator.dashboard.title": "แดชบอร์ด",
  "operator.dashboard.period": "{start} ถึง {end}",
  "operator.dashboard.loadFailed": "โหลดแดชบอร์ดไม่สำเร็จ",
  "operator.dashboard.new": "ร้านค้าใหม่",
  "operator.dashboard.newHint": "เพิ่มเข้ามาในช่วงนี้",
  "operator.dashboard.cumulative": "สะสม",
  "operator.dashboard.cumulativeHint": "ยอดรวมทั้งหมดในรายชื่อ",
  "operator.dashboard.active": "ใช้งานจริง",
  "operator.dashboard.activeHint": "มียอดรายการชำระมากกว่าศูนย์",
  "operator.dashboard.activeRate": "อัตราการใช้งาน",
  "operator.dashboard.noPreviousPeriod": "ไม่มีช่วงก่อนหน้า",
  "operator.dashboard.monthOnMonth": "{value} เทียบกับเดือนก่อน",
  "operator.dashboard.revenue": "รายได้จากค่าสมาชิก",
  "operator.dashboard.transactionsHint": "การชำระค่าสมาชิก",
  "operator.dashboard.transactionAmount": "ยอดรายการชำระ",
  "operator.dashboard.pendingSettlement": "รอเคลียร์ยอด",
  "operator.dashboard.settled": "เคลียร์ยอดแล้ว",
  "operator.dashboard.allTime": "ตลอดเวลา",
  "operator.dashboard.cumulativeRevenue": "รายได้สะสมทั้งหมด",
  "operator.dashboard.pendingPlusSettled": "ยอดที่รออยู่รวมกับที่เคลียร์แล้ว",

  "operator.merchants.title": "ร้านค้า",
  "operator.merchants.description": "ร้านค้าทั้งหมดบนแพลตฟอร์ม พร้อมแพ็กเกจและสถานะสมาชิกปัจจุบัน",
  "operator.merchants.empty": "ไม่มีร้านค้าที่ตรงกับคำค้นนี้",
  "operator.merchants.search": "ค้นหาร้านค้า",
  "operator.merchants.subscription": "การสมัครสมาชิก",
  "operator.merchants.notProvisioned": "ยังไม่ได้เปิดใช้",
  "operator.merchants.anySubscription": "การสมัครสมาชิกทั้งหมด",
  "operator.merchants.anyStatus": "สถานะทั้งหมด",
  "operator.merchants.new": "ร้านค้าใหม่",
  "operator.merchants.loadFailed": "โหลดข้อมูลร้านค้านี้ไม่สำเร็จ",
  "operator.merchants.created": "{country} · สร้างเมื่อ {date}",
  "operator.merchants.signContract": "ทำสัญญา",
  "operator.merchants.profile": "ข้อมูลทั่วไป",
  "operator.merchants.pilot": "ร้านค้าทดลอง",
  "operator.merchants.channelPartner": "พาร์ตเนอร์ตัวแทน",
  "operator.merchants.direct": "ตรง",
  "operator.merchants.bdStaff": "เจ้าหน้าที่พัฒนาธุรกิจ",
  "operator.merchants.afterSalesStaff": "เจ้าหน้าที่ดูแลหลังการขาย",
  "operator.merchants.settlementTimezone": "เขตเวลาสำหรับเคลียร์ยอด",
  "operator.merchants.internalRemarks": "บันทึกภายใน",
  "operator.merchants.subscriptions": "การสมัครสมาชิก",
  "operator.merchants.perMonthExpires": "{price} ต่อเดือน · หมดอายุ {date}",
  "operator.merchants.manage": "จัดการ",
  "operator.merchants.nothingProvisioned":
    "ยังไม่ได้เปิดใช้อะไรเลย ทำสัญญาเพื่อขายแพ็กเกจให้ร้านค้านี้",
  "operator.merchants.contracts": "สัญญา",
  "operator.merchants.contractSigned": "{plan} · ลงนามเมื่อ {date}",
  "operator.merchants.noContracts": "ยังไม่มีสัญญา",
  "operator.merchants.users": "ผู้ใช้ระบบหลังร้าน",
  "operator.merchants.createFailed": "สร้างร้านค้าไม่สำเร็จ",
  "operator.merchants.create": "สร้างร้านค้า",
  "operator.merchants.creating": "กำลังสร้าง…",
  "operator.merchants.name": "ชื่อร้านค้า",
  "operator.merchants.countryHint": "สาขาแรกจะตั้งค่าตามตลาดนี้: {currencies}, {locales}",
  "operator.merchants.countryHintEmpty": "กำหนดโปรไฟล์ตลาดที่ใช้ตั้งค่าสาขาแรก",
  "operator.merchants.currencyPair": "{first} และ {second}",
  "operator.merchants.selectMarket": "เลือกตลาด",
  "operator.merchants.firstStore": "สาขาแรก",
  "operator.merchants.storeName": "ชื่อสาขา",
  "operator.merchants.webAddress": "ที่อยู่เว็บ",
  "operator.merchants.webAddressHint": "ใช้กับเว็บสั่งอาหารผ่าน QR ตัวพิมพ์เล็ก ตัวเลข และขีดกลาง",
  "operator.merchants.ownerUsername": "ชื่อผู้ใช้ของเจ้าของร้าน",
  "operator.merchants.ownerName": "ชื่อเจ้าของร้าน",
  "operator.merchants.ownerEmail": "อีเมลเจ้าของร้าน",
  "operator.merchants.ownerPassword": "รหัสผ่านเจ้าของร้าน",
  "operator.merchants.ownerPasswordHint":
    "อย่างน้อย 12 ตัวอักษร ส่งมอบอย่างปลอดภัย เพราะจะไม่แสดงอีก",

  "operator.contracts.title": "สัญญา",
  "operator.contracts.description":
    "ลงนามจากหน้าของร้านค้า สัญญาที่ส่งแล้วแก้ไขไม่ได้ — การเปลี่ยนแปลงคือการต่ออายุ การอัปเกรด หรือสัญญาใหม่",
  "operator.contracts.empty": "ไม่มีสัญญา",
  "operator.contracts.number": "สัญญา",
  "operator.contracts.signed": "ลงนามเมื่อ",
  "operator.contracts.term": "ระยะเวลา",
  "operator.contracts.termValue": "{months} เดือน ถึง {date}",
  "operator.contracts.total": "รวม",
  "operator.contracts.signTitle": "ทำสัญญาให้ {merchant}",
  "operator.contracts.locked":
    "สัญญาที่ลงนามแล้วแก้ไขไม่ได้ การเปลี่ยนแปลงภายหลังคือการต่ออายุ การอัปเกรด หรือสัญญาใหม่",
  "operator.contracts.sign": "ลงนามสัญญา",
  "operator.contracts.signing": "กำลังลงนาม…",
  "operator.contracts.signFailed": "ทำสัญญาไม่สำเร็จ",
  "operator.contracts.planVersion": "เวอร์ชันแพ็กเกจ",
  "operator.contracts.choosePlan": "เลือกแพ็กเกจ",
  "operator.contracts.planOption": "{name} — {price}/เดือน",
  "operator.contracts.billedTo": "เรียกเก็บกับ",
  "operator.contracts.billedToHint": "สัญญาระดับเครือร้านจะใช้โควตาร่วมกันทุกสาขาในกลุ่ม",
  "operator.contracts.oneStore": "สาขาเดียว",
  "operator.contracts.wholeChain": "ทั้งเครือร้าน",
  "operator.contracts.store": "สาขา",
  "operator.contracts.cycle": "รอบชำระ",
  "operator.contracts.yearlyAnnual": "รายปี เก็บครั้งเดียวต่อปี",
  "operator.contracts.duration": "ระยะเวลาเป็นเดือน",
  "operator.contracts.signDate": "วันที่ลงนาม",
  "operator.contracts.signDateHint": "การสมัครสมาชิกเริ่มในวันนี้",
  "operator.contracts.trialMonths": "จำนวนเดือนทดลองใช้ฟรี",
  "operator.contracts.trialMonthsHint": "เพิ่มจากระยะเวลาสัญญา แพ็กเกจจะกลับมาเองเมื่อช่วงทดลองจบ",
  "operator.contracts.discount": "ส่วนลด",
  "operator.contracts.discountHint": "เป็นหน่วยย่อยของ {currency}",
  "operator.contracts.discountHintUnknown": "เป็นหน่วยย่อยของสกุลเงินตามแพ็กเกจ",
  "operator.contracts.estimate": "ยอดรวมโดยประมาณ {total} เซิร์ฟเวอร์จะคิดราคาใหม่อีกครั้งตอนส่ง",

  "operator.subscriptions.title": "การสมัครสมาชิก",
  "operator.subscriptions.description": "สิ่งที่ร้านค้าแต่ละรายถืออยู่ตอนนี้ และเงื่อนไขที่ขายไป",
  "operator.subscriptions.empty": "ไม่มีการสมัครสมาชิก",
  "operator.subscriptions.loadFailed": "โหลดข้อมูลการสมัครสมาชิกนี้ไม่สำเร็จ",
  "operator.subscriptions.heading": "{merchant} — {plan}",
  "operator.subscriptions.subheading": "ระดับ {tier} · {scope}",
  "operator.subscriptions.invalidate": "ยกเลิกสิทธิ์",
  "operator.subscriptions.renew": "ต่ออายุ",
  "operator.subscriptions.changePlan": "เปลี่ยนแพ็กเกจ",
  "operator.subscriptions.addon": "ส่วนเสริม",
  "operator.subscriptions.term": "ระยะเวลา",
  "operator.subscriptions.storedStatus": "สถานะที่บันทึกไว้",
  "operator.subscriptions.datesDisagree": "(วันที่บอกอีกอย่าง)",
  "operator.subscriptions.starts": "เริ่ม",
  "operator.subscriptions.trialEnds": "ช่วงทดลองจบ",
  "operator.subscriptions.termLength": "ความยาวระยะเวลา",
  "operator.subscriptions.months": "{months} เดือน",
  "operator.subscriptions.priceSnapshot": "ราคาที่บันทึกไว้",
  "operator.subscriptions.perMonth": "{price} ต่อเดือน",
  "operator.subscriptions.quotaSnapshot": "โควตาที่บันทึกไว้",
  "operator.subscriptions.unmetered": "ไม่จำกัด",
  "operator.subscriptions.quotaWithExtra": "{allowed} ({limit} + ซื้อเพิ่ม {extra})",
  "operator.subscriptions.features": "ฟีเจอร์ ({count})",
  "operator.subscriptions.featurePermanent": "ถาวร",
  "operator.subscriptions.addons": "ส่วนเสริม",
  "operator.subscriptions.noAddons": "ยังไม่ได้ซื้อ",
  "operator.subscriptions.addonQuota": "{resource} × {quantity}",
  "operator.subscriptions.addonUntil": "ถึง {date}",
  "operator.subscriptions.renewTitle": "ต่ออายุการสมัครสมาชิก",
  "operator.subscriptions.addonTitle": "ซื้อส่วนเสริม",
  "operator.subscriptions.apply": "ยืนยัน",
  "operator.subscriptions.applying": "กำลังดำเนินการ…",
  "operator.subscriptions.applyFailed": "ดำเนินการเปลี่ยนแปลงไม่สำเร็จ",
  "operator.subscriptions.priceIt": "คิดราคา",
  "operator.subscriptions.priceFailed": "คิดราคาการเปลี่ยนแปลงไม่สำเร็จ",
  "operator.subscriptions.monthsLabel": "จำนวนเดือน",
  "operator.subscriptions.renewCycleHint":
    "การต่ออายุก่อนกำหนดจะนับต่อจากวันหมดอายุเดิม ระยะเวลาที่เหลือจึงไม่หายไป",
  "operator.subscriptions.newPlan": "แพ็กเกจใหม่",
  "operator.subscriptions.newPlanHint": "เลือกได้เฉพาะระดับเท่ากันหรือสูงกว่า ลดระดับไม่ได้",
  "operator.subscriptions.planTierOption": "{name} (ระดับ {tier})",
  "operator.subscriptions.charged": "เก็บ {amount} สำหรับระยะเวลาที่เหลือ",
  "operator.subscriptions.credited": "คืนให้ {amount} จากแพ็กเกจปัจจุบัน",
  "operator.subscriptions.net": "สุทธิ {amount}",
  "operator.subscriptions.addonType": "ประเภทส่วนเสริม",
  "operator.subscriptions.extraQuota": "โควตาเพิ่ม",
  "operator.subscriptions.valueAddedFeature": "ฟีเจอร์ที่ขายแยก",
  "operator.subscriptions.resource": "ทรัพยากร",
  "operator.subscriptions.extraUnits": "จำนวนที่เพิ่ม",
  "operator.subscriptions.feature": "ฟีเจอร์",
  "operator.subscriptions.featureHint": "คิดเงินตามจำนวนวันที่เหลือของระยะเวลาหลัก",
  "operator.subscriptions.chooseFeature": "เลือกฟีเจอร์",
  "operator.subscriptions.invalidateTitle": "ยกเลิกสิทธิ์การสมัครสมาชิกของ {merchant}",
  "operator.subscriptions.invalidateWarning":
    "การกระทำนี้ย้อนกลับไม่ได้ ร้านค้าจะเสียฟีเจอร์และโควตาทั้งหมดทันที รวมถึงสิ่งที่ซื้อขาดไว้ และจะต่ออายุกลับมาไม่ได้อีก",
  "operator.subscriptions.reason": "เหตุผล",
  "operator.subscriptions.reasonHint": "บันทึกไว้กับการสมัครสมาชิกนี้ อย่างน้อยสิบตัวอักษร",
  "operator.subscriptions.requestCode": "ขอรหัส",
  "operator.subscriptions.codeFailed": "ขอรหัสไม่สำเร็จ",
  "operator.subscriptions.invalidating": "กำลังยกเลิกสิทธิ์…",
  "operator.subscriptions.invalidateFailed": "ยกเลิกสิทธิ์ไม่สำเร็จ",
  "operator.subscriptions.verificationCode": "รหัสยืนยัน",
  "operator.subscriptions.codeHintDev": "รุ่นสำหรับนักพัฒนา: กรอกรหัสให้แล้ว",
  "operator.subscriptions.codeHintSent": "ส่งไปยังช่องทางที่คุณลงทะเบียนไว้",

  "operator.plans.title": "เวอร์ชันแพ็กเกจ",
  "operator.plans.description":
    "ราคา โควตา และฟีเจอร์ที่รวมอยู่เป็นข้อมูล การแก้ไขไม่เปลี่ยนสิ่งที่การสมัครสมาชิกซึ่งลงนามไปแล้วซื้อไว้",
  "operator.plans.empty": "ไม่มีเวอร์ชันแพ็กเกจ",
  "operator.plans.search": "ค้นหาแพ็กเกจ",
  "operator.plans.yearlyPerMonth": "รายปี คิดต่อเดือน",
  "operator.plans.annualSaving": "ส่วนที่ประหยัดได้ต่อปี",
  "operator.plans.quotas": "โควตา",
  "operator.plans.features": "ฟีเจอร์",

  "operator.features.title": "ฟีเจอร์",
  "operator.features.description":
    "บันทึกเชิงพาณิชย์ของแคตตาล็อก ตัวรหัสเองเป็นโครงสร้าง — เป็นสิ่งที่ด่านตรวจของ API ใช้ตรวจสอบ — จึงแก้ไขที่นี่ไม่ได้",
  "operator.features.empty": "ไม่มีฟีเจอร์",
  "operator.features.search": "ค้นหาฟีเจอร์",
  "operator.features.feature": "ฟีเจอร์",
  "operator.features.group": "กลุ่ม",
  "operator.features.grades": "ขายเป็นระดับ",
  "operator.features.ungraded": "เปิดหรือปิด",
  "operator.features.valueAdded": "ขายแยก",

  "operator.hardware.title": "อุปกรณ์",
  "operator.hardware.description": "รวมเข้าไปในสัญญาได้",
  "operator.hardware.empty": "ไม่มีอุปกรณ์",
  "operator.hardware.search": "ค้นหาอุปกรณ์",
  "operator.hardware.name": "อุปกรณ์",
  "operator.hardware.model": "รุ่น",

  "operator.transactions.title": "รายการชำระ",
  "operator.transactions.description":
    "การชำระค่าสมาชิก ไม่ใช่ยอดขายของร้านอาหาร นี่คือบัญชีที่อยู่เบื้องหลังตัวเลขรายได้บนแดชบอร์ด",
  "operator.transactions.empty": "ไม่มีรายการชำระ",
  "operator.transactions.serial": "เลขที่",
  "operator.transactions.product": "สินค้า",
  "operator.transactions.amount": "จำนวนเงิน",
  "operator.transactions.paid": "ชำระเมื่อ",
  "operator.transactions.settlement": "การเคลียร์ยอด",
  "operator.transactions.reconciled": "กระทบยอดแล้ว",
  "operator.transactions.markReconciled": "ทำเครื่องหมายว่ากระทบยอดแล้ว",

  "operator.partners.title": "พาร์ตเนอร์ตัวแทน",
  "operator.partners.description": "ตัวแทนขายที่ได้ค่าคอมมิชชันจากค่าสมาชิกที่ร้านค้าของตนจ่าย",
  "operator.partners.empty": "ไม่มีพาร์ตเนอร์ตัวแทน",
  "operator.partners.search": "ค้นหาพาร์ตเนอร์",
  "operator.partners.partnerId": "รหัสพาร์ตเนอร์",
  "operator.partners.commission": "ค่าคอมมิชชัน",
  "operator.partners.bonus": "โบนัสการเซ็นสัญญาครั้งแรก",
  "operator.partners.settles": "เคลียร์ยอด",
  "operator.partners.settlesValue": "{from} → {to}",

  "operator.settlements.title": "การเคลียร์ยอด",
  "operator.settlements.description":
    "ค่าคอมมิชชันที่แพลตฟอร์มค้างตัวแทนขาย การปิดรอบหนึ่งต้องมีผู้ตรวจสอบที่ระบุไว้และรหัสยืนยัน และลบทิ้งไม่ได้",
  "operator.settlements.empty": "ไม่มีรอบการเคลียร์ยอด",
  "operator.settlements.number": "การเคลียร์ยอด",
  "operator.settlements.period": "ช่วงเวลา",
  "operator.settlements.periodValue": "{start} – {end}",
  "operator.settlements.gross": "ยอดรายการชำระ",
  "operator.settlements.rate": "อัตรา",
  "operator.settlements.due": "ค้างจ่าย",
  "operator.settlements.paid": "จ่ายแล้ว",
  "operator.settlements.auditor": "ผู้ตรวจสอบ",

  "operator.referrals.title": "การแนะนำร้าน",
  "operator.referrals.description": "ร้านค้าที่ถูกแนะนำมาจากเครื่องของร้านค้ารายอื่น",
  "operator.referrals.empty": "ยังไม่มีการแนะนำร้าน",
  "operator.referrals.business": "กิจการ",
  "operator.referrals.contact": "ผู้ติดต่อ",
  "operator.referrals.phone": "โทรศัพท์",
  "operator.referrals.referredBy": "แนะนำโดย",
  "operator.referrals.received": "รับเรื่องเมื่อ",

  "operator.team.title": "ทีมงาน",
  "operator.team.description": "พนักงานของแพลตฟอร์มและบัญชีของพาร์ตเนอร์ตัวแทน",
  "operator.team.empty": "ไม่มีบัญชีผู้ดูแลระบบ",
  "operator.team.name": "ชื่อ",
  "operator.team.username": "ชื่อผู้ใช้",
  "operator.team.email": "อีเมล",
  "operator.team.role": "บทบาท",
  "operator.team.scope": "ขอบเขต",
  "operator.team.ownPartner": "ร้านค้าของพาร์ตเนอร์ตนเอง",
  "operator.team.platform": "แพลตฟอร์ม",
  "operator.team.search": "ค้นหาทีมงาน",
  "operator.team.new": "ผู้ดูแลระบบใหม่",
  "operator.team.edit": "แก้ไขผู้ดูแลระบบ",
  "operator.team.record": "ข้อมูล",
  "operator.team.password": "รหัสผ่าน",
  "operator.team.passwordHint": "อย่างน้อยสิบสองตัวอักษร",
  "operator.team.setPassword": "ตั้งรหัสผ่าน",
  "operator.team.setPasswordHint":
    "จะออกจากระบบทุกที่ รวมถึงตัวคุณเองด้วย หากคุณกำลังตั้งรหัสผ่านของตัวเองใหม่",
  "operator.team.deleteTitle": "ลบผู้ดูแลระบบนี้หรือไม่?",
  "operator.team.deleteBody":
    "พวกเขาจะไม่สามารถเข้าสู่ระบบได้อีก และชื่อผู้ใช้กับอีเมลของพวกเขาจะว่างสำหรับบัญชีใหม่ ชื่อของพวกเขายังคงอยู่ในร้านค้าที่พวกเขาลงนามและทุกแถวการตรวจสอบที่พวกเขาเขียน",

  "operator.roles.title": "บทบาท",
  "operator.roles.description":
    "บทบาทของผู้ดูแลระบบ ให้ได้เฉพาะสิทธิ์ของผู้ดูแลระบบเท่านั้น — สิทธิ์ของฝั่งร้านค้าที่ให้ตรงนี้จะเป็นสิทธิ์ที่ไม่มีอะไรตรวจสอบเลย",
  "operator.roles.empty": "ไม่มีบทบาท",
  "operator.roles.role": "บทบาท",
  "operator.roles.seeded": "ตั้งมาให้",
  "operator.roles.members": "สมาชิก",
  "operator.roles.permissions": "สิทธิ์",
  "operator.roles.granted": "ให้ไว้ {count} สิทธิ์",
  "operator.roles.new": "เพิ่มบทบาท",
  "operator.roles.name": "ชื่อ",
  "operator.roles.note": "หมายเหตุ",
  "operator.roles.deleteTitle": "ลบบทบาทนี้หรือไม่",
  "operator.roles.deleteBody":
    "จะไม่มีใครได้รับมอบหมายบทบาทนี้อีก และสิทธิ์ทั้งหมดจะถูกลบออก รหัสของบทบาทนี้จะสามารถนำกลับมาใช้ใหม่ได้",
  "operator.roles.noneGranted": "ไม่ได้ให้สิทธิ์ใด",
  "operator.roles.catalogueFailed": "โหลดรายการสิทธิ์ไม่สำเร็จ จึงแสดงแต่ละสิทธิ์ด้วยคีย์",
  "operator.roles.reassignPrompt": "ปัจจุบันถือครองโดย {count} คน ย้ายไปบทบาทอื่นก่อนแล้วจึงลบ",
  "operator.roles.reassignTo": "ย้ายไปที่",
  "operator.roles.chooseRole": "เลือกบทบาท",
  "operator.roles.reassignConfirm": "ย้ายแล้วลบ",

  "operator.audit.title": "บันทึกการตรวจสอบ",
  "operator.audit.description":
    "ทุกการกระทำของผู้ดูแลระบบที่ทำให้เงินเคลื่อนไหวหรือเปลี่ยนราคา เพิ่มได้อย่างเดียว — ไม่มีปลายทางสำหรับเขียนทับ",
  "operator.audit.empty": "ยังไม่มีอะไรถูกบันทึก",
  "operator.audit.when": "เมื่อไร",
  "operator.audit.who": "ใคร",
  "operator.audit.action": "การกระทำ",
  "operator.audit.entity": "รายการ",
  "operator.audit.changedFields": "ฟิลด์ที่เปลี่ยน",

  "chainStatus.active": "ใช้งานอยู่",
  "chainStatus.suspended": "ถูกระงับ",
  "chainStatus.invalidated": "ถูกยกเลิกสิทธิ์",

  "storeStatus.active": "ใช้งานอยู่",
  "storeStatus.suspended": "ถูกระงับ",
  "storeStatus.closed": "ปิดแล้ว",

  "userStatus.active": "ใช้งานอยู่",
  "userStatus.disabled": "ถูกปิดใช้งาน",

  "partnerStatus.active": "ใช้งานอยู่",
  "partnerStatus.suspended": "ถูกระงับ",

  "planStatus.active": "ใช้งานอยู่",
  "planStatus.invalidated": "เลิกขายแล้ว",

  "contractStatus.draft": "ฉบับร่าง",
  "contractStatus.submitted": "ส่งแล้ว",
  "contractStatus.active": "ใช้งานอยู่",
  "contractStatus.completed": "เสร็จสิ้น",
  "contractStatus.cancelled": "ยกเลิกแล้ว",

  "billingScope.store": "สาขา",
  "billingScope.chain": "เครือร้าน",

  "productKind.plan": "แพ็กเกจ",
  "productKind.addon": "ส่วนเสริม",
  "productKind.hardware": "อุปกรณ์",
  "productKind.package": "ชุดรวม",

  "transactionSettlementStatus.pending": "รอดำเนินการ",
  "transactionSettlementStatus.settled": "เคลียร์ยอดแล้ว",

  "partnerSettlementStatus.draft": "ฉบับร่าง",
  "partnerSettlementStatus.pending_audit": "รอการตรวจสอบ",
  "partnerSettlementStatus.completed": "เสร็จสิ้น",
  "partnerSettlementStatus.failed": "ล้มเหลว",

  "referralStatus.new": "ใหม่",
  "referralStatus.contacted": "ติดต่อแล้ว",
  "referralStatus.converted": "ปิดการขายแล้ว",
  "referralStatus.rejected": "ถูกปฏิเสธ",

  "featureGroup.cashier": "แคชเชียร์",
  "featureGroup.data": "รายงาน",
  "featureGroup.store_settings": "ตั้งค่าสาขา",
  "featureGroup.marketing": "การตลาด",
  "featureGroup.item_management": "เมนู",
  "featureGroup.order_handling": "ออร์เดอร์",
  "featureGroup.settlement": "การเคลียร์ยอด",
  "featureGroup.inventory": "สต๊อก",
  "featureGroup.reservation": "การจอง",
  "featureGroup.chain": "เครือร้าน",

  "grade.basic": "พื้นฐาน",
  "grade.premium": "พรีเมียม",
  // Permission labels — role screens render the catalogue's key through here
  // rather than the catalogue's English `name`.
  "permission.items.read": "ดูรายการอาหาร",
  "permission.items.write": "สร้างและแก้ไขรายการอาหาร",
  "permission.items.delete": "ลบรายการอาหาร",
  "permission.items.price": "เปลี่ยนราคารายการอาหาร",
  "permission.categories.read": "ดูหมวดหมู่",
  "permission.categories.write": "สร้างและแก้ไขหมวดหมู่",
  "permission.categories.delete": "ลบหมวดหมู่",
  "permission.modifiers.read": "ดูกลุ่มตัวเลือก ท็อปปิง และป้ายกำกับ",
  "permission.modifiers.write": "แก้ไขกลุ่มตัวเลือก ท็อปปิง และป้ายกำกับ",
  "permission.modifiers.delete": "ลบกลุ่มตัวเลือก ท็อปปิง และป้ายกำกับ",
  "permission.tables.read": "ดูโต๊ะและโซน",
  "permission.tables.write": "สร้างและแก้ไขโต๊ะและโซน",
  "permission.tables.delete": "ลบโต๊ะและโซน",
  "permission.orders.read": "ดูออร์เดอร์",
  "permission.orders.write": "รับและแก้ไขออร์เดอร์",
  "permission.orders.export": "ส่งออกข้อมูลออร์เดอร์",
  "permission.orders.discount": "ให้ส่วนลด",
  "permission.orders.void": "ยกเลิกออร์เดอร์หรือรายการ",
  "permission.orders.refund": "คืนเงิน",
  "permission.orders.reverse": "ย้อนการชำระที่ปิดบิลแล้ว",
  "permission.orders.tax_exempt": "ปิดบิลแบบยกเว้นภาษี",
  "permission.orders.tip_adjust": "แก้ไขทิปหลังชำระเงินแล้ว",
  "permission.hr.read": "ดูงานและบันทึกเวลาทำงาน",
  "permission.hr.write": "จัดการงานและแก้ไขบันทึกเวลาทำงาน",
  "permission.hr.clock": "ลงเวลาเข้าและออกงาน",
  "permission.hr.tip_finalise": "สรุปปิดยอดการแบ่งทิป",
  "permission.chain.royalty": "กำหนดค่าธรรมเนียมและเรียกเก็บเงิน",
  "permission.reservations.read": "ดูการจองและรายชื่อรอคิว",
  "permission.reservations.write": "รับจองและจัดที่นั่ง",
  "permission.giftcards.read": "ค้นหาบัตรของขวัญ",
  "permission.giftcards.write": "ออกและยกเลิกบัตรของขวัญ",
  "permission.settlement.pay": "รับชำระเงิน",
  "permission.settlement.drawer": "เปิดลิ้นชักเงิน",
  "permission.shifts.read": "ดูประวัติกะ",
  "permission.shifts.write": "เปิดและส่งมอบกะ",
  "permission.staff.read": "ดูพนักงาน",
  "permission.staff.write": "สร้างและแก้ไขพนักงาน",
  "permission.staff.delete": "ลบพนักงาน",
  "permission.roles.read": "ดูบทบาท",
  "permission.roles.write": "สร้างและแก้ไขบทบาท",
  "permission.settings.read": "ดูการตั้งค่าร้าน",
  "permission.settings.store": "แก้ไขข้อมูลร้าน",
  "permission.settings.tax": "แก้ไขกฎภาษี",
  "permission.settings.charges": "แก้ไขค่าบริการและค่าธรรมเนียมโต๊ะ",
  "permission.settings.currency": "แก้ไขสกุลเงินและอัตราแลกเปลี่ยน",
  "permission.settings.payment_methods": "แก้ไขวิธีชำระเงิน",
  "permission.printers.read": "ดูเครื่องพิมพ์และประวัติการพิมพ์",
  "permission.printers.write": "แก้ไข จับคู่ และยกเลิกการจับคู่เครื่องพิมพ์",
  "permission.printers.reprint": "พิมพ์เอกสารซ้ำ",
  "permission.terminals.pair": "จับคู่เครื่องขาย",
  "permission.terminals.assign_staff": "มอบหมายพนักงานให้เครื่องขาย",
  "permission.templates.read": "ดูแม่แบบใบเสร็จ",
  "permission.templates.write": "แก้ไขแม่แบบใบเสร็จ",
  "permission.banners.read": "ดูแบนเนอร์",
  "permission.banners.write": "สร้างและแก้ไขแบนเนอร์",
  "permission.banners.delete": "ลบแบนเนอร์",
  "permission.marketing.read": "ดูคูปองและโปรโมชัน",
  "permission.marketing.write": "สร้างและแก้ไขคูปองและโปรโมชัน",
  "permission.members.read": "ดูสมาชิก",
  "permission.members.write": "สร้างและแก้ไขสมาชิก",
  "permission.members.balance": "เติมเงินและปรับยอดคงเหลือของสมาชิก",
  "permission.members.points": "ปรับคะแนนสมาชิก",
  "permission.reports.read": "ดูรายงาน",
  "permission.reports.export": "ส่งออกรายงาน",
  "permission.reports.cost": "ดูรายงานต้นทุนและกำไร",
  "permission.audit.read": "ดูบันทึกการใช้งาน",
  "permission.billing.read": "ดูแพ็กเกจสมาชิก",
  "permission.billing.manage": "ลงนาม ต่ออายุ และซื้อส่วนเสริม",
  "permission.chain.read": "ดูข้อมูลสรุปทั้งเครือ",
  "permission.chain.write": "ตั้งค่าสาขาในเครือ",
  "permission.admin.merchants.read": "ดูร้านค้า",
  "permission.admin.merchants.write": "สร้างและแก้ไขร้านค้า",
  "permission.admin.plans.read": "ดูเวอร์ชันแพ็กเกจและฟีเจอร์",
  "permission.admin.plans.write": "แก้ไขเวอร์ชันแพ็กเกจและฟีเจอร์",
  "permission.admin.contracts.read": "ดูสัญญา",
  "permission.admin.contracts.sign": "ลงนามและต่ออายุสัญญา",
  "permission.admin.subscriptions.read": "ดูการสมัครสมาชิก",
  "permission.admin.subscriptions.write": "เปิดใช้งาน อัปเกรด และต่ออายุการสมัครสมาชิก",
  "permission.admin.subscriptions.invalidate": "ยกเลิกสิทธิ์การสมัครสมาชิก",
  "permission.admin.hardware.read": "ดูอุปกรณ์",
  "permission.admin.hardware.write": "แก้ไขอุปกรณ์",
  "permission.admin.partners.read": "ดูพาร์ตเนอร์ตัวแทน",
  "permission.admin.partners.write": "แก้ไขพาร์ตเนอร์ตัวแทน",
  "permission.admin.settlements.read": "ดูการเคลียร์ยอดกับพาร์ตเนอร์",
  "permission.admin.settlements.complete": "ทำการเคลียร์ยอดกับพาร์ตเนอร์ให้เสร็จสมบูรณ์",
  "permission.admin.reconciliation.read": "ดูการกระทบยอด",
  "permission.admin.team.read": "ดูทีมงานแพลตฟอร์ม",
  "permission.admin.team.write": "แก้ไขทีมงานแพลตฟอร์มและบทบาท",
  "permission.admin.dashboard.read": "ดูแดชบอร์ดแพลตฟอร์ม",
  "permission.admin.referrals.read": "ดูการแนะนำร้าน",
  "permission.admin.referrals.write": "อัปเดตสถานะและผู้ดูแลการแนะนำร้าน",
  "permission.psi.warehouse.read": "ดูคลังสินค้า",
  "permission.psi.warehouse.write": "สร้างและแก้ไขคลังสินค้า",
  "permission.psi.supply.read": "ดูวัสดุ หน่วยนับ หมวดหมู่ และแบรนด์",
  "permission.psi.supply.write": "สร้างและแก้ไขวัสดุ หน่วยนับ หมวดหมู่ และแบรนด์",
  "permission.psi.supplier.read": "ดูผู้จัดจำหน่าย",
  "permission.psi.supplier.write": "สร้างและแก้ไขผู้จัดจำหน่าย",
  "permission.psi.purchase.read": "ดูใบสั่งซื้อ",
  "permission.psi.purchase.write": "สร้างและแก้ไขใบสั่งซื้อ",
  "permission.psi.purchase.approve": "อนุมัติหรือยกเลิกใบสั่งซื้อ",
  "permission.psi.inbound.read": "ดูใบรับเข้า",
  "permission.psi.inbound.write": "สร้างและแก้ไขใบรับเข้า",
  "permission.psi.inbound.approve": "อนุมัติหรือปฏิเสธใบรับเข้า",
  "permission.psi.outbound.read": "ดูใบเบิกออก",
  "permission.psi.outbound.write": "สร้างและแก้ไขใบเบิกออก",
  "permission.psi.outbound.approve": "อนุมัติใบเบิกออก",
  "permission.psi.count.read": "ดูใบตรวจนับ",
  "permission.psi.count.write": "ทำการตรวจนับสต๊อก",
  "permission.psi.count.adjust": "บันทึกการปรับปรุงสต๊อก",
  "permission.psi.stock.read": "ดูยอดคงเหลือและล็อตสต๊อก",
  "permission.psi.recipe.write": "เชื่อมโยงรายการอาหารกับวัสดุ",
  "permission.psi.requisition.approve": "ตรวจสอบใบเบิกของสาขาย่อย",

  "operationAction.billing.renew": "ต่ออายุการสมัครสมาชิกแล้ว",
  "operationAction.campaign.create": "สร้างแคมเปญแล้ว",
  "operationAction.campaign.delete": "ลบแคมเปญแล้ว",
  "operationAction.campaign.update": "แก้ไขแคมเปญแล้ว",
  "operationAction.cancel": "ยกเลิกการจองแล้ว",
  "operationAction.chain.capabilities_changed": "เปลี่ยนสิทธิ์การใช้งานของสาขาแล้ว",
  "operationAction.chain.menu_pushed": "ส่งเมนูไปยังสาขาแล้ว",
  "operationAction.chain.requisition_approved": "อนุมัติใบขอเบิกแล้ว",
  "operationAction.chain.requisition_rejected": "ปฏิเสธใบขอเบิกแล้ว",
  "operationAction.chain.store_added": "เพิ่มสาขาเข้าเครือร้านแล้ว",
  "operationAction.chain.user_access_changed": "เปลี่ยนสิทธิ์การเข้าถึงสาขาแล้ว",
  "operationAction.cost.create": "เพิ่มค่าใช้จ่ายแล้ว",
  "operationAction.cost.delete": "ลบค่าใช้จ่ายแล้ว",
  "operationAction.cost.update": "แก้ไขค่าใช้จ่ายแล้ว",
  "operationAction.coupon.issue": "ออกคูปองแล้ว",
  "operationAction.coupon_template.copy": "คัดลอกแม่แบบคูปองแล้ว",
  "operationAction.coupon_template.create": "สร้างแม่แบบคูปองแล้ว",
  "operationAction.coupon_template.update": "แก้ไขแม่แบบคูปองแล้ว",
  "operationAction.create": "สร้างการจองแล้ว",
  "operationAction.gift_card.issue": "ออกบัตรของขวัญแล้ว",
  "operationAction.gift_card.top_up": "เติมเงินบัตรของขวัญแล้ว",
  "operationAction.gift_card.void": "ยกเลิกบัตรของขวัญแล้ว",
  "operationAction.inventory.inbound_approved": "อนุมัติใบรับสินค้าเข้าแล้ว",
  "operationAction.inventory.inbound_rejected": "ปฏิเสธใบรับสินค้าเข้าแล้ว",
  "operationAction.inventory.outbound_issued": "ออกใบสั่งสินค้าออกแล้ว",
  "operationAction.inventory.purchase_notified": "ส่งใบสั่งซื้อไปยังผู้จัดจำหน่ายแล้ว",
  "operationAction.inventory.purchase_raised": "สร้างใบสั่งซื้อแล้ว",
  "operationAction.inventory.purchase_received": "รับใบสั่งซื้อแล้ว",
  "operationAction.inventory.purchase_voided": "ยกเลิกใบสั่งซื้อแล้ว",
  "operationAction.inventory.recipe_changed": "เปลี่ยนสูตรของรายการแล้ว",
  "operationAction.inventory.stock_adjusted": "ปรับสต๊อกแล้ว",
  "operationAction.inventory.supplier_prices_changed": "เปลี่ยนราคาผู้จัดจำหน่ายแล้ว",
  "operationAction.inventory.transfer_accepted": "ยอมรับการโอนแล้ว",
  "operationAction.inventory.transfer_rejected": "ปฏิเสธการโอนแล้ว",
  "operationAction.item.batch": "แก้ไขรายการหลายรายการพร้อมกันแล้ว",
  "operationAction.item.create": "เพิ่มรายการแล้ว",
  "operationAction.item.delete": "ลบรายการแล้ว",
  "operationAction.item.meal_set_changed": "เปลี่ยนชุดอาหารแล้ว",
  "operationAction.item.update": "แก้ไขรายการแล้ว",
  "operationAction.job.create": "เพิ่มตำแหน่งงานแล้ว",
  "operationAction.job.delete": "ลบตำแหน่งงานแล้ว",
  "operationAction.job.update": "แก้ไขตำแหน่งงานแล้ว",
  "operationAction.no_show": "บันทึกการจองว่าไม่มาแล้ว",
  "operationAction.notifications.update": "แก้ไขการตั้งค่าการแจ้งเตือนแล้ว",
  "operationAction.payment_method.create": "เพิ่มวิธีชำระเงินแล้ว",
  "operationAction.payment_method.delete": "ลบวิธีชำระเงินแล้ว",
  "operationAction.print.reprint": "พิมพ์ใบเสร็จซ้ำแล้ว",
  "operationAction.printer.bind": "จับคู่เครื่องพิมพ์แล้ว",
  "operationAction.printer.create": "เพิ่มเครื่องพิมพ์แล้ว",
  "operationAction.printer.unbind": "ยกเลิกการจับคู่เครื่องพิมพ์แล้ว",
  "operationAction.promotion.create": "สร้างโปรโมชันแล้ว",
  "operationAction.promotion.delete": "ลบโปรโมชันแล้ว",
  "operationAction.promotion.update": "แก้ไขโปรโมชันแล้ว",
  "operationAction.receipt_template.update": "แก้ไขแม่แบบใบเสร็จแล้ว",
  "operationAction.role.create": "สร้างบทบาทแล้ว",
  "operationAction.role.update": "แก้ไขบทบาทแล้ว",
  "operationAction.royalty.run": "คำนวณค่าลิขสิทธิ์แล้ว",
  "operationAction.royalty_charge.finalise": "สรุปค่าลิขสิทธิ์แล้ว",
  "operationAction.royalty_scheme.create": "สร้างแผนค่าลิขสิทธิ์แล้ว",
  "operationAction.royalty_scheme.set_stores": "เปลี่ยนสาขาของแผนค่าลิขสิทธิ์แล้ว",
  "operationAction.royalty_scheme.update": "แก้ไขแผนค่าลิขสิทธิ์แล้ว",
  "operationAction.seat": "จัดที่นั่งให้ลูกค้าแล้ว",
  "operationAction.service_charge_rate.create": "เพิ่มอัตราค่าบริการแล้ว",
  "operationAction.service_charge_rate.delete": "ลบอัตราค่าบริการแล้ว",
  "operationAction.service_charge_rate.update": "แก้ไขอัตราค่าบริการแล้ว",
  "operationAction.settings.delivery_providers_changed": "เปลี่ยนผู้ให้บริการจัดส่งแล้ว",
  "operationAction.settings.invoice_changed": "เปลี่ยนการเรียงเลขใบกำกับภาษีแล้ว",
  "operationAction.settlement.drawer_opened": "เปิดลิ้นชักเก็บเงินแล้ว",
  "operationAction.shift.close": "ปิดกะแล้ว",
  "operationAction.specification_group.create": "สร้างกลุ่มตัวเลือกแล้ว",
  "operationAction.specification_group.delete": "ลบกลุ่มตัวเลือกแล้ว",
  "operationAction.specification_group.update": "แก้ไขกลุ่มตัวเลือกแล้ว",
  "operationAction.staff.create": "เพิ่มพนักงานแล้ว",
  "operationAction.staff.delete": "ลบพนักงานแล้ว",
  "operationAction.staff.photo_delete": "ลบรูปพนักงานแล้ว",
  "operationAction.staff.photo_set": "ตั้งรูปพนักงานแล้ว",
  "operationAction.staff.reset_pin": "รีเซ็ต PIN พนักงานแล้ว",
  "operationAction.staff.take_till": "รับเครื่องแคชเชียร์ด้วยการสแกนแล้ว",
  "operationAction.staff.assign_till": "ได้รับมอบหมายเครื่องแคชเชียร์โดยผู้จัดการ",
  "operationAction.staff.update": "แก้ไขพนักงานแล้ว",
  "operationAction.staff.set_password": "ตั้งรหัสผ่านพนักงานแล้ว",
  "operationAction.store_group.create": "สร้างกลุ่มสาขาแล้ว",
  "operationAction.store_group.delete": "ลบกลุ่มสาขาแล้ว",
  "operationAction.store_group.set_stores": "เปลี่ยนสาขาในกลุ่มแล้ว",
  "operationAction.store_group.update": "แก้ไขกลุ่มสาขาแล้ว",
  "operationAction.store_profile.update": "แก้ไขข้อมูลร้านแล้ว",
  "operationAction.table_fee.update": "แก้ไขค่าธรรมเนียมโต๊ะแล้ว",
  "operationAction.tax_rate.create": "เพิ่มอัตราภาษีแล้ว",
  "operationAction.tax_rate.delete": "ลบอัตราภาษีแล้ว",
  "operationAction.tax_rate.update": "แก้ไขอัตราภาษีแล้ว",
  "operationAction.tax_settings.update": "แก้ไขการตั้งค่าภาษีแล้ว",
  "operationAction.time_entry.correct": "แก้ไขบันทึกเวลาทำงานแล้ว",
  "operationAction.tip_distribution.finalise": "สรุปการแบ่งทิปแล้ว",
  "operationAction.tip_pool.create": "สร้างกองทิปแล้ว",
  "operationAction.tip_pool.delete": "ลบกองทิปแล้ว",
  "operationAction.tip_pool.run": "คำนวณกองทิปแล้ว",
  "operationAction.tip_pool.set_jobs": "เปลี่ยนตำแหน่งงานในกองทิปแล้ว",
  "operationAction.tip_pool.update": "แก้ไขกองทิปแล้ว",
  "operationAction.tip_settings.update": "แก้ไขการตั้งค่าทิปแล้ว",
  "operationAction.topping_template.create": "สร้างแม่แบบท็อปปิ้งแล้ว",
  "operationAction.topping_template.delete": "ลบแม่แบบท็อปปิ้งแล้ว",
  "operationAction.topping_template.update": "แก้ไขแม่แบบท็อปปิ้งแล้ว",
  "operationAction.topup_tier.create": "สร้างระดับเติมเงินแล้ว",
  "operationAction.topup_tier.delete": "ลบระดับเติมเงินแล้ว",
  "operationAction.trading_config.update": "แก้ไขเวลาทำการแล้ว",
} satisfies MessagePack;

/**
 * Every key, because the whole pack is machine-drafted and none of it has been
 * read by a Thai speaker yet.
 *
 * It mirrors `ES_AWAITING_REVIEW`, which shipped in the same state — and like
 * that one it is a **declaration, not a mechanism**: nothing reads it, and a
 * reviewer works from the file rather than from this list. It exists so the
 * claim is in the repo rather than in a commit message, and so the day somebody
 * reviews a section they have somewhere to record it by shrinking this.
 */
export const TH_AWAITING_REVIEW: readonly string[] = Object.keys(th).filter(
  (key) => key !== "app.name",
);
